import $ from 'jquery';
// Plotly is provided by vendor.js as a global, no need to import
import { hasWorkspaceShell } from './utils/page-context.js';
import { showSnackbarSafely } from './utils/snackbar.js';
import {
  getPlotlyPayloadIssue,
  isPlotlyTablePayload,
  coercePlotlyTraces,
  filterRenderableGraphFrames,
  normalizePlotlyBinaryArrays,
  stripUnsupportedPlotlyImages,
} from './utils/plotly-payload.js';
import { ensurePlotlySupportsTraces } from './utils/plotly-loader.js';
import { fetchGraphEndpoint } from './utils/graph-request.js';
import { getResponsivePlotlyLayout } from './graph-utils.js';
import { renderTableInto } from './dataset-editor.js';

var hasLoaded = false;
const MAX_QUERIES_PER_DAY = 3;
const QUERY_COUNT_COOKIE = 'felix_query_count';
const QUERY_DATE_COOKIE = 'felix_query_date';
let hasWarnedPlotlyImageStripping = false;

// Function declarations from templates.js that are used in this file
// (Functions are imported via templates.js - no need for var declarations)
// Store queries and edits for each graph so we can retry rendering
var graphRetryInfo = {};

// Cache plotly JSON per graphId so copy-as-PNG and download-CSV buttons can access it
const graphPlotlyCache = new Map();
window.graphPlotlyCache = graphPlotlyCache;

function checkQueryLimit() {
  const today = new Date().toDateString();
  const lastQueryDate = getCookie(QUERY_DATE_COOKIE);
  let queryCount = parseInt(getCookie(QUERY_COUNT_COOKIE) || '0');

  // Reset count if it's a new day
  if (lastQueryDate !== today) {
    setCookie(QUERY_DATE_COOKIE, today, 1);
    setCookie(QUERY_COUNT_COOKIE, '0', 1);
    queryCount = 0;
  }

  queryCount++;
  setCookie(QUERY_COUNT_COOKIE, queryCount.toString(), 1);

  if (userData.is_subscribed) {
    return true;
  }

  if (userData.show_subscription_dialog) {
    $('#subscribe-dialog').get(0).showModal();
    return false;
  }

  if (queryCount > MAX_QUERIES_PER_DAY) {
    $('#subscribe-dialog').get(0).showModal();
    return false;
  }
  return true;
}

// Cookie helper functions
function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = 'expires=' + d.toUTCString();
  document.cookie = name + '=' + value + ';' + expires + ';path=/';
}

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
}

// --- Mobile lazy-render helpers for Plotly ---
function isInViewport(el) {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const vpH = window.innerHeight || document.documentElement.clientHeight;
  const vpW = window.innerWidth || document.documentElement.clientWidth;
  return rect.top < vpH && rect.bottom > 0 && rect.left < vpW && rect.right > 0;
}

function showGraphRenderError(target, message, detail) {
  const el = (typeof target === 'string') ? document.getElementById(target) : target;
  const safeMessage = message || 'Could not render this visualization.';
  if (el) {
    el.innerHTML = '';
    const errorDiv = document.createElement('div');
    errorDiv.className = 'graph-error';

    const heading = document.createElement('h3');
    heading.textContent = 'Error rendering visualization';
    errorDiv.appendChild(heading);

    const messageNode = document.createElement('p');
    messageNode.className = 'error-message';
    messageNode.textContent = safeMessage;
    errorDiv.appendChild(messageNode);

    const helpNode = document.createElement('p');
    helpNode.className = 'error-help';
    helpNode.textContent = detail || 'Try a different visualization type or dataset.';
    errorDiv.appendChild(helpNode);

    el.appendChild(errorDiv);
  }
  showSnackbarSafely({ message: safeMessage, timeout: 5000 });
}

function getGraphErrorMessage(payload, fallback) {
  const defaultMessage = fallback || 'I could not build that chart. Select a dataset and try again.';
  if (!payload || typeof payload !== 'object') return defaultMessage;

  const primary = payload.message || payload.error_detail;
  if (typeof primary === 'string' && primary.trim()) return primary.trim();

  if (typeof payload.error === 'string' && payload.error.trim()) {
    if (payload.error === 'NO_DATA_FOUND') {
      return 'No data was found. Upload a file or select a dataset, then run the question again.';
    }
    return payload.error.trim();
  }
  return defaultMessage;
}

function getGraphErrorHelp(payload) {
  const recovery = payload && payload.recovery;
  if (recovery && typeof recovery.guidance === 'string' && recovery.guidance.trim()) {
    return recovery.guidance.trim();
  }
  const suggestions = recovery && Array.isArray(recovery.suggestions) ? recovery.suggestions : [];
  const first = suggestions.length ? suggestions[0] : null;
  if (first && typeof first.query === 'string' && first.query.trim()) {
    return `Try: ${first.query.trim()}`;
  }
  return 'Check the selected dataset and ask with the metric, grouping, or chart type you want.';
}

function restoreMainQuery(queryValue) {
  const input = document.getElementById('query');
  if (input && !String(input.value || '').trim() && String(queryValue || '').trim()) {
    input.value = queryValue;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

async function renderPlotlyNow(target, data, layout, options) {
  const el = (typeof target === 'string') ? document.getElementById(target) : target;
  if (!el) return;
  if (!el.style.minHeight) {
    el.style.minHeight = '360px';
  }
  if (!el.style.width) {
    el.style.width = '100%';
  }
  data = normalizePlotlyBinaryArrays(data);
  layout = normalizePlotlyBinaryArrays(layout || {});
  const widthCandidates = [
    el.clientWidth,
    el.getBoundingClientRect().width,
    el.parentElement && el.parentElement.clientWidth,
    window.innerWidth
  ].map(Number).filter(width => Number.isFinite(width) && width > 0);
  // A fixed-width payload or grid item can make the target wider than both its
  // card and the viewport. Use the smallest real constraint for responsive
  // decisions so an already-overflowing target cannot opt itself out.
  const measuredWidth = widthCandidates.length ? Math.min(...widthCandidates) : 0;
  layout = getResponsivePlotlyLayout(layout, measuredWidth);
  if (measuredWidth > 0 && measuredWidth <= 600) {
    options = Object.assign({}, options || {});
    options.modeBarButtonsToRemove = Array.from(new Set([
      ...(options.modeBarButtonsToRemove || []),
      'pan2d',
      'zoom2d',
      'zoomIn2d',
      'zoomOut2d',
      'autoScale2d'
    ]));
  }
  const payloadIssue = getPlotlyPayloadIssue(data);
  if (payloadIssue) {
    showGraphRenderError(el, payloadIssue);
    return;
  }
  // Render the coerced trace array so single-trace objects and JSON-string
  // payloads (which pass the validation above) reach Plotly as a real array.
  let traces = coercePlotlyTraces(data) || data;
  const stripped = stripUnsupportedPlotlyImages(traces, layout || {});
  traces = stripped.traces;
  layout = stripped.layout || layout;
  if (
    !hasWarnedPlotlyImageStripping &&
    stripped &&
    (stripped.removedImageTraces || stripped.removedLayoutImages)
  ) {
    hasWarnedPlotlyImageStripping = true;
    console.warn(
      'Plotly payload contained unsupported image sources; removed before rendering.',
      {
        removedImageTraces: stripped.removedImageTraces,
        removedLayoutImages: stripped.removedLayoutImages,
      }
    );
  }
  if (!Array.isArray(traces) || traces.length === 0) {
    showGraphRenderError(
      el,
      'This visualization only contained unsupported images and could not be rendered.'
    );
    return;
  }
  try {
    const plotly = await ensurePlotlySupportsTraces(traces);
    // The target may have been removed while the full renderer was loading.
    if (el.isConnected === false) return;
    await plotly.newPlot(el, traces, layout || {}, options || {});
  } catch (error) {
    console.error('Error rendering graph:', error);
    showGraphRenderError(
      el,
      'Could not render this visualization.',
      error && error.message ? error.message : String(error || '')
    );
  }
}

let lazyPlotObserver = null;
function ensureLazyObserver() {
  if (lazyPlotObserver) return lazyPlotObserver;
  if ('IntersectionObserver' in window) {
    lazyPlotObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const el = entry.target;
        if (entry.isIntersecting && el.__pendingPlotlyJSON) {
          const cfg = el.__pendingPlotlyJSON;
          delete el.__pendingPlotlyJSON;
          el.removeAttribute('data-defer-render');
          renderPlotlyNow(el, cfg.data || [], cfg.layout || {}, cfg.options || {});
        }
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });
  }
  return lazyPlotObserver;
}

function renderPlotlyMaybeLazy(target, data, layout, options) {
  const el = (typeof target === 'string') ? document.getElementById(target) : target;
  if (!el) return;
  const isMobile = (window.innerWidth || 1024) <= 768;
  const rect = el.getBoundingClientRect();
  if (
    isMobile &&
    rect.width > 0 &&
    rect.height > 0 &&
    !isInViewport(el) &&
    'IntersectionObserver' in window
  ) {
    el.__pendingPlotlyJSON = { data, layout, options };
    el.setAttribute('data-defer-render', 'true');
    try { ensureLazyObserver().observe(el); } catch (e) {}
    return;
  }
  renderPlotlyNow(el, data, layout, options);
}

function buildCsvFromPlotly(plotlyJSON) {
  const traces = plotlyJSON.data || [];
  if (traces.length === 0) return '';

  const dataByCol = {};
  let maxLen = 0;

  function addCol(colName, arr) {
    if (arr === undefined) return;
    dataByCol[colName] = Array.isArray(arr) ? arr : [arr];
    maxLen = Math.max(maxLen, dataByCol[colName].length);
  }

  traces.forEach(function(trace, i) {
    const name = trace.name || ('trace_' + i);
    addCol('x_' + name, trace.x);
    addCol('y_' + name, trace.y);
    addCol('z_' + name, trace.z);
    addCol('values_' + name, trace.values);
    addCol('labels_' + name, trace.labels);
    addCol('text_' + name, trace.text);
  });

  const cols = Object.keys(dataByCol);

  function esc(v) {
    const s = String(v === undefined || v === null ? '' : v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
      return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  }

  const lines = [cols.map(esc).join(',')];
  for (let r = 0; r < maxLen; r++) {
    lines.push(cols.map(function(col) { return esc((dataByCol[col] || [])[r]); }).join(','));
  }
  return lines.join('\n');
}

let setupOnce = async function (evt, user) {
  if (!hasWorkspaceShell()) {
    return;
  }

  // Ensure we have user data - use passed parameter or window.userData as fallback
  const userData = user || window.userData;
  if (!userData || !userData.secret) {
    console.log('No user data available, skipping setup');
    return;
  }

  if (hasLoaded) {
    return;
  }
  hasLoaded = true;

  // Make sure window.userData is set for other parts of the app
  if (!window.userData) {
    window.userData = userData;
  }

  function getNextDefaultChatName(chats) {
    chats = chats || {};
    let maxChatNumber = 0;
    Object.keys(chats).forEach(function (chatName) {
      const match = /^Chat\s+(\d+)$/i.exec(chatName.trim());
      if (match) {
        maxChatNumber = Math.max(maxChatNumber, parseInt(match[1], 10));
      }
    });

    let nextName = 'Chat ' + (maxChatNumber + 1);
    while (chats[nextName]) {
      maxChatNumber += 1;
      nextName = 'Chat ' + (maxChatNumber + 1);
    }
    return nextName;
  }

  function getNextDefaultDashboardName(dashboards) {
    dashboards = dashboards || {};
    let maxNumber = 0;
    Object.keys(dashboards).forEach(function (name) {
      const match = /^Dashboard\s+(\d+)$/i.exec(name.trim());
      if (match) {
        maxNumber = Math.max(maxNumber, parseInt(match[1], 10));
      }
    });

    let nextName = 'Dashboard ' + (maxNumber + 1);
    while (dashboards[nextName]) {
      maxNumber += 1;
      nextName = 'Dashboard ' + (maxNumber + 1);
    }
    return nextName;
  }

  // Inline rename of a sidebar item (chat or dashboard). Triggered by
  // double-click or long-press on the `.file-name` span. Commits on Enter or
  // blur, cancels on Escape.
  function startInlineRename($fileName) {
    if (!$fileName || !$fileName.length) return;
    if ($fileName.attr('data-editing') === 'true') return;

    const $file = $fileName.closest('.file');
    const isDashboard = $file.closest('#dashboardsTreeView').length > 0;
    const oldName = ($fileName.text() || '').trim();
    const el = $fileName.get(0);

    $fileName.attr('data-editing', 'true');
    $fileName.attr('contenteditable', 'true');
    $fileName.addClass('file-name--editing');

    el.focus();
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (e) {
      // selection is best-effort
    }

    let done = false;
    function finish(commit) {
      if (done) return;
      done = true;
      const newName = ($fileName.text() || '').trim();
      $fileName.off('.inlineRename');
      $fileName.removeAttr('contenteditable');
      $fileName.removeAttr('data-editing');
      $fileName.removeClass('file-name--editing');

      if (!commit || !newName || newName === oldName) {
        $fileName.text(oldName);
        return;
      }
      if (isDashboard) {
        commitDashboardRename(oldName, newName);
      } else {
        commitChatRename(oldName, newName);
      }
    }

    $fileName.on('keydown.inlineRename', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        el.blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        finish(false);
        el.blur();
      }
    });
    $fileName.on('blur.inlineRename', function () {
      finish(true);
    });
  }

  async function commitChatRename(oldName, newName) {
    try {
      const chats = await dataAccessor.getChats(window.userData);
      if (chats && chats[newName]) {
        showSnackbarSafely({ message: 'A chat with that name already exists', timeout: 2500 });
        await renderChats();
        return;
      }
      await dataAccessor.renameChat(window.userData, oldName, newName);
      await dataAccessor.setCurrentActiveChat(window.userData, newName);
      await renderChats();
      await renderChat();
    } catch (error) {
      console.warn('Chat rename failed:', error);
      showSnackbarSafely({ message: 'Could not rename chat', timeout: 2500 });
      await renderChats();
    }
  }

  async function commitDashboardRename(oldName, newName) {
    try {
      const dashboards = await dataAccessor.getDashboards(window.userData);
      if (dashboards && dashboards[newName]) {
        showSnackbarSafely({ message: 'A dashboard with that name already exists', timeout: 2500 });
        await renderDashboards();
        return;
      }
      await dataAccessor.renameDashboard(window.userData, oldName, newName);
      await dataAccessor.setCurrentActiveDashboard(window.userData, newName);
      await renderDashboards();
      await renderDashboard();
    } catch (error) {
      console.warn('Dashboard rename failed:', error);
      showSnackbarSafely({ message: 'Could not rename dashboard', timeout: 2500 });
      await renderDashboards();
    }
  }

  function appendChatRenameActions() {
    $('#chatsTreeView .file').each(function () {
      if ($(this).find('.chat-rename-icon').length) return;
      $('<i class="chat-rename-icon material-icons" title="Rename chat" aria-label="Rename chat">edit</i>')
        .appendTo($(this));
    });
  }


  // todo if we dont havea dashboard already
  dataAccessor.getCurrentActiveDashboardName(userData).then(function (dashboardName) {
    // Double-check user data before rendering dashboards
    if (!window.userData || !window.userData.secret) {
      console.warn('User data became invalid before renderDashboards');
      return;
    }
    renderDashboards();

    // Double-check user data before getting chat name
    if (!window.userData || !window.userData.secret) {
      console.warn('User data became invalid before getCurrentActiveChatName');
      return;
    }
    dataAccessor.getCurrentActiveChatName(window.userData).then(function (chatName) {
      renderChats();
      const activeSurface = localStorage.getItem('felix_active_surface') || 'chat';
      if (activeSurface === 'dashboard') {
        renderDashboard();
      } else {
        renderChat();
      }
    });

    var dashboardsTreeView = document.getElementById('dashboardsTreeView');
    if (dashboardsTreeView) {
      dashboardsTreeView.addEventListener('dragover', function (e) {
        e.preventDefault();
      });

      dashboardsTreeView.addEventListener('drop', function (e) {
        e.preventDefault();
        if (!e.dataTransfer.files || e.dataTransfer.files.length === 0) {
          return;
        }

        var file = e.dataTransfer.files[0];
        if (!file.name.toLowerCase().endsWith('.txt')) {
          return;
        }

        var reader = new FileReader();
        reader.onload = function (event) {
          var text = event.target.result;
          var lines = text.split(/\r?\n/).filter(function (line) {
            return line.trim().length > 0;
          });
          if (lines.length === 0) {
            return;
          }

          var dashboardName = file.name.replace(/\.txt$/i, '');

          dataAccessor.newDashboard(window.userData, dashboardName).then(function () {
            dataAccessor
              .setCurrentActiveDashboard(window.userData, dashboardName)
              .then(function () {
                renderDashboards();
              });

            lines.forEach(function (query) {
              var graphId = Math.random().toString(36).substring(7);
              var finishedFuture = fetchAndRenderGraphForQuery(query.trim(), [], graphId);
              finishedFuture.then(function () {
                dataAccessor.addGraphToCurrentDashboard(window.userData, graphId, query.trim());
              });
            });
          });
        };
        reader.readAsText(file);
      });
    }
  });

  $(document).on('click', '.refresh-chat-message-icon', async function (e) {
    e.preventDefault();
    e.stopPropagation();
    var $el = $(this);
    var editId = $el.data('id');
    var graphId;
    var editIdx = -1;
    if (editId.indexOf('--') !== -1) {
      graphId = editId.split('--')[0];
      editIdx = +editId.split('--')[1];
    } else {
      graphId = editId;
    }
    // if theres an edit, add 1 and refresh
    if (editIdx !== -1) {
      var currentChat = await dataAccessor.getCurrentChat(window.userData);
      var messages = currentChat.messages;
      for (var mId in messages) {
        let currentMessage = messages[mId];
        if (mId === graphId) {
          var newEdits = currentMessage.edits;
          newEdits[editIdx] = newEdits[editIdx] + '-'; // bust the cache
          await dataAccessor.addEditsToChat(
            window.userData,
            currentChat.name,
            mId,
            currentMessage.messageName,
            newEdits
          );
          //update ui
          window.location.reload(); // todo proper re rendering?
        }
      }
    } else {
      var currentChat = await dataAccessor.getCurrentChat(window.userData);
      var messages = currentChat.messages;
      for (var mId in messages) {
        if (mId === graphId) {
          messages[mId].messageName = messages[mId].messageName + '-'; // bust the cache
          await dataAccessor.addEditsToChat(
            window.userData,
            currentChat.name,
            mId,
            messages[mId].messageName,
            messages[mId].edits
          );
          //update ui
          window.location.reload(); // todo proper re rendering?
        }
      }
    }
  });

  $(document).on('click', '.delete-chat-message-icon', async function (e) {
    e.preventDefault();
    e.stopPropagation();
    var $el = $(this);
    var editId = $el.data('id');
    var graphId;
    var editIdx = -1;
    if (editId.indexOf('--') !== -1) {
      graphId = editId.split('--')[0];
      editIdx = +editId.split('--')[1];
    } else {
      graphId = editId;
    }
    // if theres an edit, remove it
    if (editIdx !== -1) {
      var currentChat = await dataAccessor.getCurrentChat(window.userData);
      var messages = currentChat.messages;
      for (var mId in messages) {
        //one by one? then reorder after all complete
        let currentMessage = messages[mId];
        if (mId === graphId) {
          var newEdits =
            currentMessage.edits.slice(0, editIdx) + currentMessage.edits.slice(editIdx + 1);
          await dataAccessor.addEditsToChat(
            window.userData,
            currentChat.name,
            mId,
            currentMessage.messageName,
            newEdits
          );
          //update ui
          // if it's the end message hide, if others are after, need to recompute/re-render
          if (editIdx === currentMessage.edits.length - 1) {
            // hide ui/rerender?
            //todo
          }
          let currentComment = $el.parent();
          $('#' + editId).remove();
          currentComment.remove();
          // todo proper re rendering?
          // await fetchAndRenderChatForQuery(currentMessage.messageName, currentMessage.edits, mId);
        }
      }
    } else {
      var currentChat = await dataAccessor.getCurrentChat(window.userData);
      var messages = currentChat.messages;
      for (var mId in messages) {
        //one by one? then reorder after all complete
        if (mId === graphId) {
          await dataAccessor.deleteMessageFromChat(window.userData, currentChat.name, mId);
          break;
        }
        // hide ui/rerender?
        //todo
      }
      let currentComment = $el.parent();
      $('#' + editId).remove();
      currentComment.remove();
    }

    return false;
  });

  $(document).on('click', '#dashboardsTreeView .file-delete-icon', function (e) {
    e.preventDefault();
    e.stopPropagation();
    var $el = $(this);
    var file_name = $el.parent().find('.file-name').text();
    dataAccessor.deleteDashboard(window.userData, file_name).then(function (response) {
      renderDashboards();
      renderDashboard();
    });

    $(this).closest('.file').remove(); // remove parent
    return false;
  });

  $(document).on('click', '#chatsTreeView .file-delete-icon', function (e) {
    e.preventDefault();
    e.stopPropagation();
    var $el = $(this);
    var file_name = $el.parent().find('.file-name').text();
    dataAccessor.deleteChat(window.userData, file_name).then(function (response) {
      renderChats();
      renderDashboard(); // goto a dashboard? or new chat?
    });

    $(this).closest('.file').remove(); // remove parent
    return false;
  });

  $(document).on('submit', '#submitNewDashForm', function (e) {
    e.preventDefault();
    e.stopPropagation();

    dataAccessor.getDashboards(window.userData).then(function (dashboards) {
      var currentDashboardName = ($('#dashname').val() || '').trim();
      if (!currentDashboardName) {
        currentDashboardName = getNextDefaultDashboardName(dashboards);
      }
      if (dashboards && dashboards[currentDashboardName]) {
        showSnackbarSafely({ message: 'A dashboard with that name already exists', timeout: 2500 });
        return;
      }

      dataAccessor.newDashboard(window.userData, currentDashboardName).then(function () {
        dataAccessor
          .setCurrentActiveDashboard(window.userData, currentDashboardName)
          .then(function () {
            renderDashboard(); // entire dashboard render
          });
        dataAccessor.currentActiveDashboardName = currentDashboardName;
        renderDashboards();
        $('#dashname').val('');
        var dashDialog = document.getElementById('add-dash-dialog');
        if (dashDialog && dashDialog.open) dashDialog.close();
        //select the dashboard
        setTimeout(function () {
          $('#dashboardsTreeView .file').each(function (index, element) {
            if ($(element).find('.file-name').text() === currentDashboardName) {
              $(element).click();
            }
          });
        }, 100);
      });
    });
    // unselect all in chat treeview
    $('#chatTreeView .file').removeClass('active');
    return false;
  });

  $(document).on('submit', '#submitNewChatForm', function (e) {
    e.preventDefault();
    e.stopPropagation();

    dataAccessor.getChats(window.userData).then(function (chats) {
      var currentChatName = ($('#chatname').val() || '').trim();
      if (!currentChatName) {
        currentChatName = getNextDefaultChatName(chats);
      }
      if (chats && chats[currentChatName]) {
        showSnackbarSafely({ message: 'A chat with that name already exists', timeout: 2500 });
        return;
      }

      dataAccessor.newChat(window.userData, currentChatName).then(function () {
        dataAccessor.setCurrentActiveChat(window.userData, currentChatName).then(function () {
          renderChat(); // entire dashboard render
        });
        // currentActiveChatName = currentChatName;
        //todo render only one highlight in the treeview
        renderChats();
        $('#chatname').val('');
        var chatDialog = document.getElementById('add-chat-dialog');
        if (chatDialog && chatDialog.open) chatDialog.close();
        //select the dashboard
        $('#chatsTreeView .file').each(function (index, element) {
          if ($(element).find('.file-name').text() === currentChatName) {
            $(element).click();
          }
        });
        // unselect all in dashboard treeview
        $('#dashboardsTreeView .file').removeClass('active');
      });
    });

    return false;
  });

  async function renderDashboards() {
    // Ensure we have user data
    if (!window.userData || !window.userData.secret) {
      console.log('No user data available for renderDashboards');
      return;
    }
    var dashboardsFuture = dataAccessor.getDashboards(window.userData);
    var dashboardNamesFuture = dashboardsFuture.then(async function (dashboards) {
      if (!dashboards) {
        dashboards = {
          Untitled: {
            graphs: {
              name: 'Untitled'
            }
          }
        };
        dataAccessor.newDashboard(window.userData, 'Untitled'); //todo wait for it to tfinih
        await dataAccessor.setCurrentActiveDashboard(window.userData, 'Untitled');
      }
      let dashboardNames = Object.keys(dashboards);
      var currentActiveDashboardName = await dataAccessor.getCurrentActiveDashboardName(
        window.userData
      );
      // view logic

      createTreeView(dashboardNames, '#dashboardsTreeView', '');
      // todo fix this bad extra view step
      let $files = $('#dashboardsTreeView').find('.file');
      $files.each(function (i, item) {
        var dashboardName = $(item).find('.file-name').text();
        if (dashboardName === currentActiveDashboardName) {
          $(this).addClass('active');
        }
      });
    });
  }

  async function renderDashboard() {
    // # collect and render all the graphs in a dashboard
    var currentDashboard = await dataAccessor.getCurrentDashboard(window.userData);
    var currentDashboardName = await dataAccessor.getCurrentActiveDashboardName(window.userData);

    // Defensive: keep rendering usable even if storage returns nothing
    if (!currentDashboard || typeof currentDashboard !== 'object') {
      console.warn('renderDashboard: no dashboard data returned, using empty dashboard');
      currentDashboard = { name: currentDashboardName || 'Untitled', graphs: {}, gridConfig: null };
    }
    if (!currentDashboard.graphs) {
      currentDashboard.graphs = {};
    }
    var graphs = currentDashboard.graphs;
    //should we save?

    // delete all graphs
    $('.grid-stack-item').remove();
    $('.chat-container')[0].innerHTML = '';
    $('.chat-container').removeClass('chat-container--open');
    $('.askfelixbutton').removeClass('askfelixbutton--chat');

    // render all graphs
    for (var graphId in graphs) {
      //one by one? then reorder after all complete
      let currentGraph = graphs[graphId];
      await fetchAndRenderGraphForQuery(currentGraph.graphName, currentGraph.edits, graphId);
      var nowDashboard = await dataAccessor.getCurrentDashboard(window.userData);
      var nowDashboardName = nowDashboard && nowDashboard.name;

      // check if the current dashboard is the same, otherwise dont put in another chart in this dashboard
      if (currentDashboardName !== nowDashboardName) {
        return;
      }
    }
    var gridConfig = currentDashboard.gridConfig;
    if (gridConfig) {
      loadGridConfigNoHTML(gridConfig);
    }
  }

  async function renderChats() {
    var chatsFuture = dataAccessor.getChats(window.userData);
    var chatNamesFuture = chatsFuture.then(async function (chats) {
      if (!chats) {
        chats = {
          Untitled: {
            chats: {
              name: 'Untitled'
            }
          }
        };
        dataAccessor.newChat(window.userData, 'Untitled'); //todo wait for it to tfinih
        await dataAccessor.setCurrentActiveChat(window.userData, 'Untitled');
      }
      let chatNames = Object.keys(chats);
      var currentActiveChatName = await dataAccessor.getCurrentActiveChatName(window.userData);
      // view logic

      createTreeView(chatNames, '#chatsTreeView', '');
      appendChatRenameActions();
      // todo fix this bad extra view step
      let $files = $('#chatsTreeView').find('.file');
      $files.each(function (i, item) {
        var chatName = $(item).find('.file-name').text();
        if (chatName === currentActiveChatName) {
          $(this).addClass('active');
        }
      });
    });
  }

  async function renderChat() {
    var currentChat = await dataAccessor.getCurrentChat(window.userData);
    if (!currentChat) {
      console.warn('Current chat not found');
      return;
    }
    var messages = currentChat.messages || [];
    //should we save?

    // delete all messages or dashboards
    $('.grid-stack-item').remove();

    // Add null check for chat container
    if ($('.chat-container').length > 0) {
      $('.chat-container')[0].innerHTML = '';
      $('.chat-container').addClass('chat-container--open');
    }

    $('.askfelixbutton').addClass('askfelixbutton--chat');
    // render all messages
    for (var mId in messages) {
      //one by one? then reorder after all complete
      let currentMessage = messages[mId];
      await fetchAndRenderChatForQuery(currentMessage.messageName, currentMessage.edits, mId);

      var currentChatPostRequestName = await dataAccessor.getCurrentActiveChatName(window.userData);
      // check if the current dashboard is the same, otherwise dont put in another chart in this dashboard
      if (currentChatPostRequestName !== currentChat.name) {
        return; // stop rendering/do something else
      }
    }
  }

  $(document).on('click', '#dashboardsTreeView .file', async function (evt) {
    // todo temp disable everything?

    // Don't switch dashboards while the name is being edited inline.
    if ($(this).find('.file-name').attr('data-editing') === 'true') return;
    var dashboardName = $(this).find('.file-name').text();
    // disable all active others
    $('#dashboardsTreeView .file').removeClass('active');
    $('#chatsTreeView .file').removeClass('active');
    $(this).addClass('active');
    await dataAccessor.setCurrentActiveDashboard(window.userData, dashboardName);
    //select current dashboard
    // todo render dashboard graphs with edits
    renderDashboard();
  });

  $(document).on('click', '#chatsTreeView .file', async function (evt) {
    // todo temp disable everything?

    // Don't switch chats while the name is being edited inline.
    if ($(this).find('.file-name').attr('data-editing') === 'true') return;
    var chatName = $(this).find('.file-name').text();
    // disable all active others
    $('#dashboardsTreeView .file').removeClass('active');
    $('#chatsTreeView .file').removeClass('active');
    $(this).addClass('active');
    await dataAccessor.setCurrentActiveChat(window.userData, chatName);
    //select current dashboard
    // todo render dashboard graphs with edits
    renderChat();
  });

  // Inline rename: double-click the name (desktop).
  $(document).on(
    'dblclick',
    '#chatsTreeView .file-name, #dashboardsTreeView .file-name',
    function (e) {
      e.preventDefault();
      e.stopPropagation();
      startInlineRename($(this));
    }
  );

  // Inline rename: long-press the name (touch / press-and-hold).
  var inlineLongPressTimer = null;
  var inlineLongPressFired = false;
  $(document).on(
    'touchstart mousedown',
    '#chatsTreeView .file-name, #dashboardsTreeView .file-name',
    function () {
      var $target = $(this);
      inlineLongPressFired = false;
      if (inlineLongPressTimer) clearTimeout(inlineLongPressTimer);
      inlineLongPressTimer = setTimeout(function () {
        inlineLongPressFired = true;
        startInlineRename($target);
      }, 550);
    }
  );
  $(document).on(
    'touchend touchmove mouseup mousemove mouseleave',
    '#chatsTreeView .file-name, #dashboardsTreeView .file-name',
    function () {
      if (inlineLongPressTimer) {
        clearTimeout(inlineLongPressTimer);
        inlineLongPressTimer = null;
      }
    }
  );
  // Swallow the click that follows a long-press so it doesn't switch surface.
  $(document).on(
    'click',
    '#chatsTreeView .file-name, #dashboardsTreeView .file-name',
    function (e) {
      if (inlineLongPressFired) {
        e.preventDefault();
        e.stopPropagation();
        inlineLongPressFired = false;
      }
    }
  );

  $(document).on('click', '#chatsTreeView .chat-rename-icon', function (e) {
    e.preventDefault();
    e.stopPropagation();
    startInlineRename($(this).closest('.file').find('.file-name'));
  });

  // Share button events
  $(document).on('click', '.share-chat-message-icon', function (e) {
    e.preventDefault();
    e.stopPropagation();

    const messageId = $(this).data('id');
    const chatName = dataAccessor.currentActiveChatName;

    // Show the share dialog for this chat message
    if (typeof graphSharing !== 'undefined') {
      graphSharing.openShareDialog(messageId, chatName, null);
    }
  });

  $(document).on('click', '.panel-share-button', function (e) {
    e.preventDefault();
    e.stopPropagation();

    const graphId = $(this).data('id');
    const dashboardName = dataAccessor.currentActiveDashboardName;

    // Show the share dialog for this dashboard graph
    if (typeof graphSharing !== 'undefined') {
      graphSharing.openShareDialog(graphId, null, dashboardName);
    }
  });

  $(document).on('click', '.panel-rename-button', function () {
    var graphId = $(this).data('id');
    var name = window.prompt('Name for this chart (leave blank to cancel):');
    if (!name || !name.trim()) return;
    name = name.trim();
    dataAccessor.addGraphToCurrentDashboard(window.userData, graphId, name)
      .catch(function (e) { console.error('Rename failed:', e); });
    showSnackbarSafely({ message: 'Chart named: ' + name, timeout: 2000 });
  });

  // todo show an undo modal breifly
  $(document).on('click', '#panel-delete-button', async function (event) {
    let gridstackItem = $(this).parents('.grid-stack-item');
    // pushAllToTopAfterRemoving(gridstackItem);
    removeNodeFromGrid(gridstackItem);
    var graphId = gridstackItem.find('.askfelixformedit').data('id');
    gridstackItem.remove();
    graphPlotlyCache.delete(graphId);
    dataAccessor.deleteGraphFromCurrentDashboard(window.userData, graphId);
    // # todo relayout the grid
    // todo save grid layout
  });

  $(document).on('click', '.panel-refresh-button', async function (event) {
    let gridstackItem = $(this).parents('.grid-stack-item');
    // pushAllToTopAfterRemoving(gridstackItem);

    // removeNodeFromGrid(gridstackItem);
    var graphId = gridstackItem.find('.askfelixformedit').data('id');
    await dataAccessor.refreshGraphFromCurrentDashboard(window.userData, graphId);
    // todo proper reloading/rerendering
    window.location.reload();
    // gridstackItem.remove();
    // dataAccessor.deleteGraphFromCurrentDashboard(window.userData, graphId);
    // # todo relayout the grid
    // todo save grid layout
  });

  $(document).on('click', '.panel-retry-button', async function (event) {
    let gridstackItem = $(this).parents('.grid-stack-item');
    var graphId = gridstackItem.find('.askfelixformedit').data('id');
    const info = graphRetryInfo[graphId];
    if (info) {
      await fetchAndRenderGraphForQuery(info.query, info.edits, graphId);
    }
  });

  $(document).on('click', '.d3-retry-button', async function (event) {
    const graphId = $(this).data('graph-id');
    const info = graphRetryInfo[graphId];
    if (info) {
      await fetchAndRenderGraphForQuery(info.query, info.edits, graphId);
    }
  });

  $(document).on('click', '#chat-delete-button', async function (event) {
    let chatItem = $(this).parents('.chat');
    var graphId = chatItem.find('.askfelixformedit').data('id');
    var currentChat = await dataAccessor.getCurrentChat(window.userData);

    chatItem.remove();

    if (currentChat && graphId) {
      await dataAccessor.deleteMessageFromChat(window.userData, currentChat.name, graphId);
    }
  });
};
$(document).on('user-ready', setupOnce);
document.addEventListener('user-ready', function (event) {
  setupOnce(event, event.detail);
});
$(function () {
  let attempts = 0;
  const trySetup = function () {
    if (window.userData && window.userData.secret) {
      setupOnce(null, window.userData);
      return true;
    }
    return false;
  };
  if (trySetup()) return;
  const timer = window.setInterval(function () {
    attempts += 1;
    if (trySetup() || attempts >= 50) {
      window.clearInterval(timer);
    }
  }, 100);
});

function responseProcessor(currentDashboardName, graphId, queryValue, edits) {
  return async function (response) {
    var nowDashboardName = await dataAccessor.getCurrentActiveDashboardName(window.userData);

    if (currentDashboardName !== nowDashboardName) {
      return;
    }

    //check if response is an image/png or jpeg
    var { isImage, data, plotlyJSON, metadata, plotlyChartType } =
      await extractResponseData(response);
    metadata = metadata || {};
    plotlyJSON = plotlyJSON || {};
    // Expose minimal metadata for suggestion context
    try {
      window.lastGraphMeta = {
        graph_type: metadata && metadata.graph_type,
        df_columns: (data && data.columns) || (metadata && metadata.df_columns) || undefined,
        df_shape: (metadata && metadata.df_shape) || undefined,
        verbalization: (metadata && metadata.verbalization) || undefined
      };
    } catch (e) {}

    // Cache the graph data for future use
    if (plotlyJSON && !plotlyJSON.error && graphCache) {
      graphCache.storeGraph(queryValue, currentDashboardName, graphId, {
        isImage,
        data: isImage ? data : null, // Don't store blob data in IndexedDB
        plotlyJSON,
        metadata,
        plotlyChartType
      });
    }
    if (plotlyJSON && !plotlyJSON.error) {
      graphPlotlyCache.set(graphId, plotlyJSON);
    }

    if (!document.getElementById(`${graphId}graph-target`)) {
      addNewHTMLToGrid(renderGraphTemplate(graphId), 6, graphId);
    }
    componentHandler.upgradeDom();
    let $formTypahead = $('.askfelixformedit[data-id="' + graphId + '"] .typeahead');
    addNewAutocompleterEditElement(
      $formTypahead,
      metadata.df_filename,
      queryValue,
      edits,
      plotlyChartType
    );

    var plotlyDiv = document.getElementById(`${graphId}graph-target`);

    // Check if plotlyDiv exists
    if (!plotlyDiv) {
      console.error(`DOM element for graph ${graphId} not found`);
      return;
    }

    // Tag chart with data size for memory management
    if (metadata && metadata.df_shape && Array.isArray(metadata.df_shape)) {
      const rows = metadata.df_shape[0] || 0;
      plotlyDiv.setAttribute('data-size', rows);

      // Mark as heavy if it exceeds threshold
      if (rows > 5000) {
        plotlyDiv.setAttribute('data-heavy', 'true');
        console.log(`Heavy chart detected (${rows} datapoints): ${graphId}`);
      }
    } else if (plotlyJSON && plotlyJSON.data) {
      // Try to estimate data size from plotly data
      let totalPoints = 0;
      plotlyJSON.data.forEach(trace => {
        if (Array.isArray(trace.x)) totalPoints += trace.x.length;
        if (Array.isArray(trace.y)) totalPoints += trace.y.length;
        if (Array.isArray(trace.z)) {
          if (Array.isArray(trace.z[0])) {
            // 2D array like heatmaps
            totalPoints += trace.z.length * trace.z[0].length;
          } else {
            totalPoints += trace.z.length;
          }
        }
      });

      plotlyDiv.setAttribute('data-size', totalPoints);
      if (totalPoints > 5000) {
        plotlyDiv.setAttribute('data-heavy', 'true');
        console.log(`Heavy chart detected (${totalPoints} datapoints): ${graphId}`);
      }
    }

    // Add pipeline logs if available
    if (metadata && metadata.pipeline_logs && metadata.pipeline_logs.length > 0) {
      // Remove existing logs if present
      $(plotlyDiv).siblings('.pipeline-logs-container').remove();

      var logsHtml = renderPipelineLogsTemplate(
        metadata.pipeline_logs,
        metadata.reasoning_trace || {},
        metadata.reasoning_judge || null
      );
      var logsDiv = document.createElement('div');
      logsDiv.innerHTML = logsHtml;
      plotlyDiv.parentNode.insertBefore(logsDiv, plotlyDiv.nextSibling);

      // Add click handler for toggling logs display
      $('.pipeline-logs-header').on('click', function () {
        $(this).next('.pipeline-logs-content').slideToggle(200);
      });
    }

    // Extract column names from data for edit options
    let columns = [];
    if (data && !isImage && typeof data === 'object' && data.columns) {
      columns = data.columns;
    } else if (plotlyJSON && plotlyJSON.data && plotlyJSON.data.length > 0) {
      // Try to extract columns from the plotly data
      const plotlyData = plotlyJSON.data[0];
      if (plotlyData.x) {
        columns.push('x');
      }
      if (plotlyData.y) {
        columns.push('y');
      }
      // Try to find other properties that might be columns
      Object.keys(plotlyData).forEach(key => {
        if (['x', 'y', 'name', 'type', 'mode', 'marker', 'line'].indexOf(key) === -1) {
          columns.push(key);
        }
      });
    }

    // If we have data from the API response, use its columns
    if (plotlyJSON && plotlyJSON.metadata && plotlyJSON.metadata.df_shape) {
      // Try to get columns from the DataFrame metadata
      const dfShape = plotlyJSON.metadata.df_shape;
      if (dfShape && Array.isArray(dfShape) && dfShape.length >= 2) {
        // We have the DataFrame shape, see if we can get the column names
        if (data && data.columns) {
          columns = data.columns;
        }
      }
    }

    // Add the edit options panel
    $(`#${graphId}-edit-options`).remove();
    var editOptionsHtml = renderGraphEditOptionsTemplate(graphId, metadata, columns);
    var editOptionsDiv = document.createElement('div');
    editOptionsDiv.innerHTML = editOptionsHtml;
    plotlyDiv.parentNode.insertBefore(editOptionsDiv, plotlyDiv);

    // Initialize the edit options with current values
    if (plotlyJSON && plotlyJSON.data && plotlyJSON.data.length > 0) {
      const plotlyData = plotlyJSON.data[0];
      if (plotlyData.x && $(`#${graphId}-x-axis option[value="${plotlyData.x}"]`).length) {
        $(`#${graphId}-x-axis`).val(plotlyData.x);
      }
      if (plotlyData.y && $(`#${graphId}-y-axis option[value="${plotlyData.y}"]`).length) {
        $(`#${graphId}-y-axis`).val(plotlyData.y);
      }
      if (
        plotlyData.marker &&
        plotlyData.marker.color &&
        $(`#${graphId}-color option[value="${plotlyData.marker.color}"]`).length
      ) {
        $(`#${graphId}-color`).val(plotlyData.marker.color);
      }
    }

    //check it data is an image
    if (isImage) {
      var blob = await data;
      // use filereader to read base64 url
      var base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      // var url = URL.createObjectURL(blob);
      var img = document.createElement('img');
      img.src = base64;
      img.style.width = '100%';
      img.style.height = '100%';
      plotlyDiv.innerHTML = '';
      plotlyDiv.appendChild(img);
    } else if (plotlyJSON['embed']) {
      renderTableInto(plotlyDiv, { payload: plotlyJSON });
    } else if (metadata['graph_type'] == 'text_summary') {
      var text = document.createElement('p');
      text.className = 'demo-content mdl-color--white felix-dashboard-chat';
      text.innerHTML =
        '<img id="ask-felix-robot-icon" class="ask-felix-robot-icon ask-felix-robot-icon--tooltip ask-felix-robot-icon--tooltip-dashboard" src="/static/img/askfelix-robot-icon-transp.webp" alt="A" style="">' +
        plotlyJSON['text'];
      plotlyDiv.innerHTML = '';
      plotlyDiv.appendChild(text); // add askfelix logo?
    } else if (metadata['graph_type'] == 'table' && !isPlotlyTablePayload(plotlyJSON)) {
      if (window.NativeDatasetEditor && typeof window.NativeDatasetEditor.renderTableInto === 'function') {
        renderTableInto(plotlyDiv, { payload: plotlyJSON });
        return;
      }
      // Render table data with enhanced functionality
      var tableContainer = document.createElement('div');
      tableContainer.className = 'table-container';

      if (plotlyJSON.data && plotlyJSON.data.length > 0) {
        var tableColumns = plotlyJSON.columns || Object.keys(plotlyJSON.data[0]);

        // Create DataTable-compatible structure
        var table = document.createElement('table');
        table.className = 'display stripe hover';
        table.style.width = '100%';
        var tableId = 'datatable-' + Math.random().toString(36).substring(7);
        table.id = tableId;

        // Create header
        var thead = document.createElement('thead');
        var headerRow = document.createElement('tr');
        tableColumns.forEach(function (column) {
          var th = document.createElement('th');
          th.textContent = column;
          headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Create body
        var tbody = document.createElement('tbody');
        plotlyJSON.data.forEach(function (row) {
          var tr = document.createElement('tr');
          tableColumns.forEach(function (column) {
            var td = document.createElement('td');
            td.textContent = row[column] || '';
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);

        tableContainer.appendChild(table);

        // Add verbalization if available
        if (plotlyJSON.verbalization) {
          var verbDiv = document.createElement('div');
          verbDiv.className = 'table-verbalization';
          verbDiv.style.marginTop = '10px';
          verbDiv.style.fontStyle = 'italic';
          verbDiv.style.color = '#666';
          verbDiv.textContent = plotlyJSON.verbalization;
          tableContainer.appendChild(verbDiv);
        }

        plotlyDiv.innerHTML = '';
        plotlyDiv.appendChild(tableContainer);

        // Initialize DataTable with enhanced features
        function initDataTable() {
          if (typeof $ !== 'undefined' && $.fn.DataTable) {
            $('#' + tableId).DataTable({
              pageLength: 25,
              lengthMenu: [
                [10, 25, 50, 100, -1],
                [10, 25, 50, 100, 'All']
              ],
              responsive: true,
              scrollX: true,
              order: [],
              columnDefs: [
                {
                  targets: '_all',
                  className: 'dt-body-left'
                }
              ],
              dom: '<"top"fli>rt<"bottom"p><"clear">',
              language: {
                search: 'Search table:',
                info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                lengthMenu: 'Show _MENU_ entries'
              }
            });
          } else {
            // Fallback: Load DataTables dynamically if not available
            var link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.datatables.net/1.13.7/css/jquery.dataTables.min.css';
            document.head.appendChild(link);

            var script = document.createElement('script');
            script.src = 'https://cdn.datatables.net/1.13.7/js/jquery.dataTables.min.js';
            script.onload = function () {
              $('#' + tableId).DataTable({
                pageLength: 25,
                lengthMenu: [
                  [10, 25, 50, 100, -1],
                  [10, 25, 50, 100, 'All']
                ],
                responsive: true,
                scrollX: true,
                order: [],
                columnDefs: [
                  {
                    targets: '_all',
                    className: 'dt-body-left'
                  }
                ],
                dom: '<"top"fli>rt<"bottom"p><"clear">',
                language: {
                  search: 'Search table:',
                  info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                  lengthMenu: 'Show _MENU_ entries'
                }
              });
            };
            document.head.appendChild(script);
          }
        }

        // Initialize table after DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initDataTable);
        } else {
          setTimeout(initDataTable, 100);
        }
      } else {
        tableContainer.innerHTML = '<div class="no-data">No table data available</div>';
        plotlyDiv.innerHTML = '';
        plotlyDiv.appendChild(tableContainer);
      }
    } else if (metadata['graph_type'] == 'singular_value') {
      var container = document.createElement('div');
      container.className = 'singular-value-container';

      var valueElement = document.createElement('div');
      valueElement.className = 'singular-value';
      valueElement.textContent = plotlyJSON['formatted_value'] || plotlyJSON['value'] || '0';

      var labelElement = document.createElement('div');
      labelElement.className = 'singular-value-label';
      labelElement.textContent = plotlyJSON['label'] || 'Value';

      container.appendChild(valueElement);
      container.appendChild(labelElement);

      plotlyDiv.innerHTML = '';
      plotlyDiv.appendChild(container);
    } else if (
      metadata['renderer'] == 'd3' ||
      metadata['graph_type'] == 'force_directed' ||
      metadata['graph_type'] == 'tree_layout' ||
      metadata['graph_type'] == 'network_graph'
    ) {
      // Handle D3 visualization
      plotlyDiv.innerHTML = '';

      try {
        // Check if we have Claude-generated HTML and JavaScript
        const html = metadata['html'];
        const javascript = metadata['javascript'];

        if (html && javascript) {
          console.log('Using Claude-generated D3 visualization code');

          // Track whether we've successfully rendered the D3 visualization
          let d3RenderSucceeded = false;

          try {
            // Create a container for the visualization
            const container = document.createElement('div');
            container.className = 'd3-visualization-container claude-generated';
            container.id = `d3-container-${graphId}`;
            container.style.width = '100%';
            container.style.height = '100%';

            // Set the HTML content
            container.innerHTML = html || '<div id="d3-viz"></div>';

            // Add a loading indicator that will be removed once D3 rendering completes
            const loadingDiv = document.createElement('div');
            loadingDiv.className = 'd3-loading';
            loadingDiv.innerHTML = `
              <div class="d3-loading-spinner"></div>
              <div>Rendering visualization...</div>
            `;
            container.appendChild(loadingDiv);

            plotlyDiv.appendChild(container);

            // Direct evaluation of JavaScript with proper error handling
            const executeVisualization = jsCode => {
              try {
                // Add visualization ID to the code for targeting
                const wrappedCode = `
                  try {
                    // Wrap in a try-catch to catch any runtime errors
                    (function() {
                      // Define container as the element where our viz should be drawn
                      const vizContainer = document.getElementById('d3-container-${graphId}');
                      
                      // Remove loading indicator
                      const loadingIndicator = vizContainer.querySelector('.d3-loading');
                      if (loadingIndicator) {
                        loadingIndicator.remove();
                      }
                      
                      // Execute the visualization code
                      ${jsCode}
                      
                      // Mark successful rendering
                      window.d3RenderSucceeded_${graphId} = true;
                    })();
                  } catch(vizError) {
                    console.error("D3 visualization runtime error:", vizError);
                    
                    // Show error in the container
                    const vizContainer = document.getElementById('d3-container-${graphId}');
                    vizContainer.innerHTML = \`
                      <div class="d3-error">
                        <h3>Visualization Error</h3>
                        <p>\${vizError.message || "Failed to render D3 visualization"}</p>
                        <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored try-plotly-fallback">
                          Try Alternative Visualization
                        </button>
                        <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored d3-retry-button" data-graph-id="${graphId}">
                          Retry
                        </button>
                      </div>
                    \`;
                    
                    // Setup click handler for fallback button
                    const fallbackBtn = vizContainer.querySelector('.try-plotly-fallback');
                    if (fallbackBtn) {
                      fallbackBtn.addEventListener('click', function() {
                        requestPlotlyFallback("${graphId}", "${metadata['graph_type'] || 'scatter'}");
                      });
                    }
                    
                    // Mark failed rendering
                    window.d3RenderSucceeded_${graphId} = false;
                  }
                `;

                // Create a script element instead of using eval directly
                const scriptElement = document.createElement('script');
                scriptElement.textContent = wrappedCode;
                document.head.appendChild(scriptElement);
                document.head.removeChild(scriptElement);

                // Check if rendering succeeded after a short delay
                setTimeout(() => {
                  if (!window[`d3RenderSucceeded_${graphId}`]) {
                    // If we get here, the code executed but didn't render properly
                    console.warn(
                      "D3 visualization execution completed but didn't render successfully"
                    );
                    requestPlotlyFallback(graphId, metadata['graph_type'] || 'scatter');
                  }
                }, 2000);

                return true;
              } catch (evalError) {
                console.error('Failed to evaluate D3 code:', evalError);

                // Show error in the container
                container.innerHTML = `
                  <div class="d3-error">
                    <h3>JavaScript Evaluation Error</h3>
                    <p>${evalError.message || 'Failed to execute visualization code'}</p>
                    <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored try-plotly-fallback">
                      Try Alternative Visualization
                    </button>
                    <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored d3-retry-button" data-graph-id="${graphId}">
                      Retry
                    </button>
                  </div>
                `;

                // Setup click handler for fallback button
                container
                  .querySelector('.try-plotly-fallback')
                  .addEventListener('click', function () {
                    requestPlotlyFallback(graphId, metadata['graph_type'] || 'scatter');
                  });

                return false;
              }
            };

            // Function to request a fallback Plotly visualization
            window.requestPlotlyFallback = function (graphId, d3GraphType) {
              // Map D3 visualization types to Plotly equivalents
              const typeMapping = {
                force_directed: 'scatter',
                network_graph: 'scatter',
                tree_layout: 'treemap',
                d3_custom: 'scatter'
              };

              // Get the original query and add chart type prefix
              const originalQuery = document.querySelector(
                `.askfelixformedit[data-id="${graphId}"] .typeahead`
              ).value;
              const plotlyType = typeMapping[d3GraphType] || 'scatter';

              // Create a new query with explicit chart type
              const fallbackQuery = `show as ${plotlyType} chart: ${originalQuery}`;

              console.log(
                `Requesting Plotly fallback for D3 ${d3GraphType} visualization using: ${fallbackQuery}`
              );

              // Show loading state
              const container = document.getElementById(`d3-container-${graphId}`);
              container.innerHTML = `
                <div class="d3-loading">
                  <div class="d3-loading-spinner"></div>
                  <div>Generating alternative visualization...</div>
                </div>
              `;

              // Make a new request with the modified query
              fetchGraphEndpoint('/query', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  query: fallbackQuery,
                  secret: window.userData.secret,
                  chart_type_override: plotlyType
                })
              })
                .then(response => response.json())
                .then(data => {
                  // Pass to the normal processing flow but skip D3 renderer
                  const mockResponse = {
                    ok: true,
                    json: () => Promise.resolve(data),
                    headers: {
                      get: () => 'application/json'
                    }
                  };

                  // Remove the D3 container
                  container.remove();

                  // Process as a normal Plotly visualization (skip D3 checks)
                  const currentDashboardName = dataAccessor.currentActiveDashboardName;
                  responseProcessor(currentDashboardName, graphId, fallbackQuery, [])(mockResponse);
                })
                .catch(error => {
                  console.error('Failed to get Plotly fallback:', error);
                  container.innerHTML = `
                  <div class="d3-error">
                    <h3>Visualization Failed</h3>
                    <p>Could not generate an alternative visualization.</p>
                  </div>
                `;
                });
            };

            // Load D3.js if needed, then execute the visualization
            if (typeof d3 === 'undefined') {
              // Load D3.js if not already available
              const d3Script = document.createElement('script');
              d3Script.src = 'https://d3js.org/d3.v7.min.js';
              d3Script.onload = function () {
                d3RenderSucceeded = executeVisualization(javascript);
              };
              document.head.appendChild(d3Script);
            } else {
              // D3 already loaded, execute our visualization code directly
              d3RenderSucceeded = executeVisualization(javascript);
            }
          } catch (e) {
            console.error('Error setting up D3 visualization:', e);

            // Show error message to user
            plotlyDiv.innerHTML = `
              <div class="d3-error">
                <h3>Visualization Setup Error</h3>
                <p>${e.message || 'Could not setup D3 visualization'}</p>
                <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored try-plotly-fallback">
                  Try Alternative Visualization
                </button>
                <button class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored d3-retry-button" data-graph-id="${graphId}">
                  Retry
                </button>
              </div>
            `;

            // Setup click handler for fallback button
            plotlyDiv.querySelector('.try-plotly-fallback').addEventListener('click', function () {
              requestPlotlyFallback(graphId, metadata['graph_type'] || 'scatter');
            });
          }
        } else {
          // Fallback to traditional d3Renderer approach
          console.log('Claude-generated D3 code not available, using traditional renderer');

          // Create a container div for the D3 visualization
          var d3Container = document.createElement('div');
          d3Container.className = 'd3-visualization-container';
          d3Container.style.width = '100%';
          d3Container.style.height = '100%';
          plotlyDiv.appendChild(d3Container);

          // Try to get the D3 configuration from one of several possible locations
          var d3Config = metadata['d3_config'];

          if (!d3Config && plotlyJSON.d3Config) {
            d3Config = plotlyJSON.d3Config;
            console.log('Using d3Config from plotlyJSON');
          }

          if (
            !d3Config &&
            plotlyJSON.data &&
            plotlyJSON.data.length > 0 &&
            plotlyJSON.data[0].nodes
          ) {
            // If we don't have d3Config but have graph data in the expected format,
            // construct a config with reasonable defaults
            console.log('Constructing d3Config from graph data');
            d3Config = {
              type: metadata['graph_type'] || 'force_directed',
              title:
                plotlyJSON.layout && plotlyJSON.layout.title
                  ? plotlyJSON.layout.title.text
                  : 'Force Directed Graph',
              data: {
                nodes: plotlyJSON.data[0].nodes || [],
                links: plotlyJSON.data[0].links || []
              },
              d3Config: {
                width: 800,
                height: 600,
                strength: -30,
                distance: 100,
                collisionRadius: 15,
                colorScheme: 'category10',
                interactive: true,
                showLabels: true
              }
            };
          }

          if (d3Config) {
            // Render the D3 visualization using the d3Renderer
            console.log('Rendering D3 visualization with config:', d3Config);
            d3Renderer.render(d3Container, d3Config);
          } else {
            console.error('No D3 configuration found for visualization');
            d3Container.innerHTML =
              "<div class='error-message'>Could not render D3 visualization: missing configuration</div>";
          }
        }
      } catch (e) {
        console.error('Error rendering D3 visualization:', e);

        // Create error message
        plotlyDiv.innerHTML = `
          <div class='error-message'>
            <h3>D3 Visualization Error</h3>
            <p>${e.message || 'Failed to render D3 visualization'}</p>
          </div>
        `;
      }
    } else if (plotlyJSON['graphs'] && plotlyJSON['graphs'].length > 0) {
      // Drop dataless frames (e.g. extra LLM/analysis frames emitted at depth+1)
      // so each one doesn't render its own "did not include Plotly traces" error
      // box. Only substitute when at least one real chart remains, so a fully
      // dataless response still surfaces its error as before.
      var __renderableGraphs = filterRenderableGraphFrames(plotlyJSON['graphs']);
      if (__renderableGraphs.length > 0) { plotlyJSON['graphs'] = __renderableGraphs; }
      // create new div for each graph
      var divs = [];
      for (var i = 0; i < plotlyJSON['graphs'].length; i++) {
        var div = document.createElement('div');
        div.id = `${graphId}graph-target${i}`;
        divs.push(div);
      }
      plotlyDiv.innerHTML = '';
      for (var i = 0; i < divs.length; i++) {
        plotlyDiv.appendChild(divs[i]);
      }

      // render each graph
      for (var i = 0; i < plotlyJSON['graphs'].length; i++) {
        var plotlyJSONLayout = getPlotlyLayoutDefaulted(plotlyJSON['graphs'][i]);
        try {
          renderPlotlyMaybeLazy(
            divs[i].id,
            plotlyJSON['graphs'][i].data,
            plotlyJSONLayout,
            plotlyDefaultOptions
          );
        } catch (e) {
          console.error('Error rendering graph:', e);
          showGraphRenderError(
            divs[i],
            'Could not render this visualization.',
            e && e.message ? e.message : String(e || '')
          );
        }
      }
    } else if (plotlyJSON.error) {
      console.error('Error in visualization data:', plotlyJSON.error);
      showGraphRenderError(
        plotlyDiv,
        getGraphErrorMessage(plotlyJSON),
        getGraphErrorHelp(plotlyJSON)
      );
    } else if (plotlyJSON.data) {
      try {
        var plotlyJSONLayout = getPlotlyLayoutDefaulted(plotlyJSON);
        renderPlotlyMaybeLazy(plotlyDiv, plotlyJSON['data'], plotlyJSONLayout, plotlyDefaultOptions);
      } catch (e) {
        console.error('Error rendering graph:', e);
        showGraphRenderError(
          plotlyDiv,
          'Could not render this visualization.',
          e && e.message ? e.message : String(e || '')
        );
      }
    } else {
      console.error('No valid graph data found in response');

      // Get any verbalization if available to show as part of the error
      const verbalization = plotlyJSON.verbalization || 'Could not find data matching your query';

      // Enhanced error message for no data
      plotlyDiv.innerHTML = `
        <div class="graph-error">
          <h3>No data available</h3>
          <p class="error-message">${verbalization}</p>
          <p class="error-help">Try modifying your query or exploring available datasets</p>
        </div>
      `;
      showSnackbarSafely({ message: verbalization, timeout: 5000 });
    }
  };
}

function showDashboardGraphLoading(graphId) {
  if (!graphId) return;
  var targetId = `${graphId}graph-target`;
  var plotlyDiv = document.getElementById(targetId);
  if (!plotlyDiv && typeof addNewHTMLToGrid === 'function' && typeof renderGraphTemplate === 'function') {
    addNewHTMLToGrid(renderGraphTemplate(graphId), 6, graphId);
    if (typeof componentHandler !== 'undefined' && componentHandler && componentHandler.upgradeDom) {
      componentHandler.upgradeDom();
    }
    plotlyDiv = document.getElementById(targetId);
  }
  if (plotlyDiv && typeof renderHelixLoaderTemplate === 'function') {
    plotlyDiv.innerHTML = renderHelixLoaderTemplate('Loading graph');
  }
}

async function fetchAndRenderGraphForQuery(queryValue, edits, graphId) {
  var askfelixbutton = $('#askfelixbutton');
  askfelixbutton.attr('disabled', 'disabled');
  showDashboardGraphLoading(graphId);

  var currentDashboardName = await dataAccessor.getCurrentActiveDashboardName(window.userData);

  // Persist query info for retrying this graph later
  graphRetryInfo[graphId] = { query: queryValue, edits: edits };

  // Check if we have this graph in the cache first
  // A selected model/reasoning setting changes the result, so do not reuse a
  // cache entry created with different AI controls. Legacy pages without the
  // controls retain their existing cache behaviour.
  var hasAiControls = ['harness', 'model', 'reasoning'].some(function (control) {
    return document.getElementById('helix-' + control);
  });
  if (graphCache && !hasAiControls) {
    try {
      const cachedGraphData = await graphCache.getGraph(queryValue, currentDashboardName, graphId);

      if (cachedGraphData) {
        console.log(`Using cached graph data for "${queryValue}"`);

        // Create a mock response object to pass to the response processor
        const mockResponse = {
          ok: true,
          json: () => Promise.resolve(cachedGraphData.plotlyJSON),
          headers: {
            get: name =>
              name.toLowerCase() === 'content-type' && cachedGraphData.isImage
                ? 'image/png'
                : 'application/json'
          },
          blob: () => Promise.resolve(cachedGraphData.data)
        };

        // Process the cached data
        await responseProcessor(currentDashboardName, graphId, queryValue, edits)(mockResponse);

        // Clean up and return early
        $('.loading-chat-bar').remove();
        askfelixbutton.removeAttr('disabled');

        return Promise.resolve();
      }
    } catch (error) {
      console.warn('Error retrieving from cache:', error);
      // Continue with the regular fetch if cache retrieval fails
    }
  }

  // If not in cache or cache failed, fetch from server
  var url = askfelixform.attr('action');
  var requestBody = {
    query: queryValue,
    secret: window.userData.secret
  };
  // Read independently and defensively for older/shared pages.
  ['harness', 'model', 'reasoning'].forEach(function (control) {
    var element = document.getElementById('helix-' + control);
    if (element && typeof element.value === 'string' && element.value.trim()) {
      requestBody[control] = element.value.trim();
    }
  });
  try {
    var requestedDataset = (
      window['currentDatasetName'] ||
      window.autocompleteDataset ||
      new URL(window.location.href).searchParams.get('dataset') ||
      ''
    ).trim();
    if (requestedDataset) {
      requestBody.datasetname = requestedDataset;
    }
  } catch (datasetError) {
    console.warn('Unable to read requested dataset for query', datasetError);
  }

  if (edits && edits.length > 0) {
    url = '/edit-graph';
    requestBody.edits = edits;
  }

  // Optionally include a next-message suggestion request
  // When enabled, fetch a follow-up suggestion and render a clickable chip above the edit field
  const shouldFetchNext = window.HELIX_ENABLE_NEXT_MESSAGE === true;
  if (shouldFetchNext) {
    try {
      // Build minimal context from last-known metadata area if available
      const lastMeta = window.lastGraphMeta || {};
      const nmPayload = {
        secret: window.userData && window.userData.secret,
        query: queryValue,
        graph_type: (lastMeta && lastMeta.graph_type) || undefined,
        columns: (lastMeta && lastMeta.df_columns) || undefined,
        df_shape: (lastMeta && lastMeta.df_shape) || undefined,
        verbalization: (lastMeta && lastMeta.verbalization) || undefined,
        table_sample: undefined,
        image_alt: undefined
      };
      fetch(fixtures.inf_server_url + '/api/query/next-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nmPayload)
      })
        .then((r) => r.json())
        .then((data) => {
          if (data && data.success && data.next_query) {
            renderNextMessageSuggestion(data.next_query, data.reason || 'Suggested next step');
          }
        })
        .catch(() => {});
    } catch (e) {
      // ignore suggestion errors
    }
  }

  var resultFuture = fetchGraphEndpoint(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(requestBody)
  })
    .then(responseProcessor(currentDashboardName, graphId, queryValue, edits))
    .then(function (processed) {
      // A deep-linked dataset (?dataset=<slug>) is auto-added to the user's
      // files server-side on first query. Refresh the "my datasets" tree so it
      // appears immediately instead of only after a page reload.
      if (requestBody.datasetname) {
        try {
          document.dispatchEvent(new CustomEvent('datasets:refresh'));
        } catch (refreshErr) {
          console.warn('Unable to refresh datasets after query', refreshErr);
        }
      }
      return processed;
    })
    .catch(function (error) {
      console.error('Error fetching graph:', error);
      restoreMainQuery(queryValue);
      var plotlyDiv = document.getElementById(`${graphId}graph-target`);
      if (plotlyDiv) {
        showGraphRenderError(
          plotlyDiv,
          'Failed to load graph',
          error && error.message ? error.message : String(error || '')
        );
      }
      showSnackbarSafely({
        message: 'Failed to load graph',
        actionHandler: function () {
          fetchAndRenderGraphForQuery(queryValue, edits, graphId);
        },
        actionText: 'Retry'
      });
      $('.loading-chat-bar').remove();
      askfelixbutton.removeAttr('disabled');
    })
    .finally(function () {
      $('.loading-chat-bar').remove();
      askfelixbutton.removeAttr('disabled');
    });

  return resultFuture;
}

if (typeof window !== 'undefined') {
  window.fetchAndRenderGraphForQuery = fetchAndRenderGraphForQuery;
  window.responseProcessor = responseProcessor;
  window.chatResponseProcessor = chatResponseProcessor;
  window.chatEditResponseProcessor = chatEditResponseProcessor;
}

function renderNextMessageSuggestion(nextQuery, reason) {
  try {
    // Prefer the inline slot near per-graph edit form, fallback to dialog remote
    var slot = document.querySelector('.next-message-slot');
    var container = slot || document.getElementById('remote') || document.body;
    var existing = document.getElementById('next-message-suggestion');
    if (existing) {
      existing.remove();
    }
    var wrapper = document.createElement('div');
    wrapper.id = 'next-message-suggestion';
    wrapper.style.margin = '8px 0';
    wrapper.innerHTML = `
      <span class="mdl-chip mdl-chip--contact" style="cursor:pointer; background:#e8f0fe;">
        <span class="mdl-chip__contact mdl-color--blue mdl-color-text--white">?</span>
        <span class="mdl-chip__text" title="${escapeHtml(reason)}">${escapeHtml(nextQuery)}</span>
      </span>
    `;
    wrapper.addEventListener('click', function() {
      try {
        // Locate the nearest edit form and submit it with the suggestion
        var form = container.closest('form');
        if (!form) {
          // Fallback: try global selectors
          form = document.querySelector('.askfelixformedit') || document.querySelector('.askfelixformeditchat') || document.getElementById('editdataaskfelixform');
        }
        if (form) {
          var input = form.querySelector('#editquery') || form.querySelector('#editchatquery') || form.querySelector('#editdataquery') || form.querySelector('.typeahead.tt-input');
          if (input) {
            input.value = nextQuery;
          }
          // Trigger the existing submit handler so it goes to /edit-graph
          if (window.$ && typeof window.$ === 'function') {
            window.$(form).trigger('submit');
          } else {
            form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
          }
        }
      } catch (e) {}
    });
    container.prepend(wrapper);
  } catch (e) {}
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

async function fetchAndRenderChatForQuery(queryValue, edits, graphId) {
  var askfelixbutton = $('#askfelixbutton');
  askfelixbutton.attr('disabled', 'disabled');
  $('.chat-container').append(renderChatMessageTemplate(queryValue, graphId));
  $(document).scrollTop($(document).height());
  $('.mdl-layout__content').scrollTop($(document).height());
  var resultFuture = fetchAndRenderChatForQueryInternal(queryValue, edits, graphId);
  resultFuture
    .then(function () {
      $('.mdl-progress').hide();
      $('.loading-chat-bar').remove();
      askfelixbutton.removeAttr('disabled');
    })
    .catch(function (error) {
      var errorMessage = error && error.message
        ? error.message
        : 'I could not build that chart. Select a dataset and try again.';

      $('.chat-container').append(renderChatFelixTemplate(errorMessage));
      $(document).scrollTop($(document).height());
      $('.mdl-layout__content').scrollTop($(document).height());
      // $('#results').html('Error: ' + error);
      $('.mdl-progress').hide();
      $('.loading-chat-bar').remove();
      askfelixbutton.removeAttr('disabled');
      console.log(error);
    });
  return resultFuture;
}

function chatResponseProcessor(currentChat, graphId, queryValue, edits, askfelixbutton) {
  return async function (response) {
    $('.mdl-progress').hide();
    $('.loading-chat-bar').remove();

    askfelixbutton.removeAttr('disabled');

    var currentChatPostRequestName = await dataAccessor.getCurrentActiveChatName(window.userData);
    // check if the current dashboard is the same, otherwise dont put in another chart in this dashboard
    if (currentChatPostRequestName !== currentChat.name) {
      return;
    }
    //check if response is an image/png or jpeg
    var { isImage, data, plotlyJSON, metadata, plotlyChartType } =
      await extractResponseData(response);
    metadata = metadata || {};
    plotlyJSON = plotlyJSON || {};
    try {
      window.lastGraphMeta = {
        graph_type: metadata && metadata.graph_type,
        df_columns: (data && data.columns) || (metadata && metadata.df_columns) || undefined,
        df_shape: (metadata && metadata.df_shape) || undefined,
        verbalization: (metadata && metadata.verbalization) || undefined
      };
    } catch (e) {}

    if (plotlyJSON.error) {
      addNewHTMLToChat(getChatTemplate(graphId), graphId);
      componentHandler.upgradeDom();
      const errorTarget = document.getElementById(`${graphId}graph-target`);
      const errorMessage = getGraphErrorMessage(plotlyJSON);
      showGraphRenderError(errorTarget, errorMessage, getGraphErrorHelp(plotlyJSON));
      if ($('.chat-container').length > 0) {
        $('.chat-container').append(renderChatFelixTemplate(errorMessage));
      }
      return;
    }

    // Cache only renderable chat data. Recovery payloads should be retried after
    // the user selects data instead of becoming sticky client-side failures.
    if (plotlyJSON && !plotlyJSON.error && graphCache) {
      graphCache.storeGraph(queryValue, currentChat.name, graphId, {
        isImage,
        data: isImage ? data : null, // Don't store blob data in IndexedDB
        plotlyJSON,
        metadata,
        plotlyChartType
      });
    }
    if (plotlyJSON && !plotlyJSON.error) {
      graphPlotlyCache.set(graphId, plotlyJSON);
    }

    addNewHTMLToChat(getChatTemplate(graphId), graphId);
    componentHandler.upgradeDom(); // setup loading bar
    let $formTypahead = $('.askfelixformeditchat[data-id="' + graphId + '"] .typeahead');
    addNewAutocompleterEditElement(
      $formTypahead,
      metadata.df_filename,
      queryValue,
      edits,
      plotlyChartType
    );

    var plotlyDiv = document.getElementById(`${graphId}graph-target`);

    // Check if plotlyDiv exists
    if (!plotlyDiv) {
      console.error(`DOM element for graph ${graphId} not found`);
      return;
    }

    // Add verbalization as a chat message if available (but only in chat mode, not in dashboards)
    if (plotlyJSON && plotlyJSON.verbalization && $('.chat-container').length > 0) {
      $('.chat-container').append(renderChatFelixTemplate(plotlyJSON.verbalization));
      // Speak the verbalization aloud on-device if the user enabled speech.
      try {
        if (window.FelixTTS) {
          window.FelixTTS.speak(plotlyJSON.verbalization);
        }
      } catch (e) {
        /* speech is best-effort */
      }
      $(document).scrollTop($(document).height());
      $('.mdl-layout__content').scrollTop($(document).height());
    }

    // Add pipeline logs if available
    if (metadata && metadata.pipeline_logs && metadata.pipeline_logs.length > 0) {
      // Remove existing logs if present
      $(plotlyDiv).siblings('.pipeline-logs-container').remove();

      var logsHtml = renderPipelineLogsTemplate(
        metadata.pipeline_logs,
        metadata.reasoning_trace || {},
        metadata.reasoning_judge || null
      );
      var logsDiv = document.createElement('div');
      logsDiv.innerHTML = logsHtml;
      plotlyDiv.parentNode.insertBefore(logsDiv, plotlyDiv.nextSibling);

      // Add click handler for toggling logs display
      $('.pipeline-logs-header').on('click', function () {
        $(this).next('.pipeline-logs-content').slideToggle(200);
      });
    }

    //check it data is an image
    if (isImage) {
      var blob = await data;
      // use filereader to read base64 url
      var base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      // var url = URL.createObjectURL(blob);
      var img = document.createElement('img');
      img.src = base64;
      img.style.width = '100%';
      img.style.height = '100%';
      plotlyDiv.innerHTML = '';
      plotlyDiv.appendChild(img);
    } else if (plotlyJSON['embed']) {
      renderTableInto(plotlyDiv, { payload: plotlyJSON });
    } else if (metadata['graph_type'] == 'text_summary') {
      var text = document.createElement('p');
      text.className = 'demo-content mdl-color--white felix-dashboard-chat';
      text.innerHTML =
        '<img id="ask-felix-robot-icon" class="ask-felix-robot-icon ask-felix-robot-icon--tooltip ask-felix-robot-icon--tooltip-dashboard" src="/static/img/askfelix-robot-icon-transp.webp" alt="A" style="">' +
        plotlyJSON['text'];
      plotlyDiv.innerHTML = '';

      plotlyDiv.appendChild(text); // add askfelix logo?
    } else if (metadata['graph_type'] == 'table' && !isPlotlyTablePayload(plotlyJSON)) {
      if (window.NativeDatasetEditor && typeof window.NativeDatasetEditor.renderTableInto === 'function') {
        renderTableInto(plotlyDiv, { payload: plotlyJSON });
        return;
      }
      // Render table data with enhanced functionality
      var tableContainer = document.createElement('div');
      tableContainer.className = 'table-container';

      if (plotlyJSON.data && plotlyJSON.data.length > 0) {
        var tableColumns = plotlyJSON.columns || Object.keys(plotlyJSON.data[0]);

        // Create DataTable-compatible structure
        var table = document.createElement('table');
        table.className = 'display stripe hover';
        table.style.width = '100%';
        var tableId = 'datatable-' + Math.random().toString(36).substring(7);
        table.id = tableId;

        // Create header
        var thead = document.createElement('thead');
        var headerRow = document.createElement('tr');
        tableColumns.forEach(function (column) {
          var th = document.createElement('th');
          th.textContent = column;
          headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Create body
        var tbody = document.createElement('tbody');
        plotlyJSON.data.forEach(function (row) {
          var tr = document.createElement('tr');
          tableColumns.forEach(function (column) {
            var td = document.createElement('td');
            td.textContent = row[column] || '';
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);

        tableContainer.appendChild(table);

        // Add verbalization if available
        if (plotlyJSON.verbalization) {
          var verbDiv = document.createElement('div');
          verbDiv.className = 'table-verbalization';
          verbDiv.style.marginTop = '10px';
          verbDiv.style.fontStyle = 'italic';
          verbDiv.style.color = '#666';
          verbDiv.textContent = plotlyJSON.verbalization;
          tableContainer.appendChild(verbDiv);
        }

        plotlyDiv.innerHTML = '';
        plotlyDiv.appendChild(tableContainer);

        // Initialize DataTable with enhanced features
        function initDataTable() {
          if (typeof $ !== 'undefined' && $.fn.DataTable) {
            $('#' + tableId).DataTable({
              pageLength: 25,
              lengthMenu: [
                [10, 25, 50, 100, -1],
                [10, 25, 50, 100, 'All']
              ],
              responsive: true,
              scrollX: true,
              order: [],
              columnDefs: [
                {
                  targets: '_all',
                  className: 'dt-body-left'
                }
              ],
              dom: '<"top"fli>rt<"bottom"p><"clear">',
              language: {
                search: 'Search table:',
                info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                lengthMenu: 'Show _MENU_ entries'
              }
            });
          } else {
            // Fallback: Load DataTables dynamically if not available
            var link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.datatables.net/1.13.7/css/jquery.dataTables.min.css';
            document.head.appendChild(link);

            var script = document.createElement('script');
            script.src = 'https://cdn.datatables.net/1.13.7/js/jquery.dataTables.min.js';
            script.onload = function () {
              $('#' + tableId).DataTable({
                pageLength: 25,
                lengthMenu: [
                  [10, 25, 50, 100, -1],
                  [10, 25, 50, 100, 'All']
                ],
                responsive: true,
                scrollX: true,
                order: [],
                columnDefs: [
                  {
                    targets: '_all',
                    className: 'dt-body-left'
                  }
                ],
                dom: '<"top"fli>rt<"bottom"p><"clear">',
                language: {
                  search: 'Search table:',
                  info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                  lengthMenu: 'Show _MENU_ entries'
                }
              });
            };
            document.head.appendChild(script);
          }
        }

        // Initialize table after DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initDataTable);
        } else {
          setTimeout(initDataTable, 100);
        }
      } else {
        tableContainer.innerHTML = '<div class="no-data">No table data available</div>';
        plotlyDiv.innerHTML = '';
        plotlyDiv.appendChild(tableContainer);
      }
    } else if (plotlyJSON['graphs'] && plotlyJSON['graphs'].length > 0) {
      // Drop dataless frames (e.g. extra LLM/analysis frames emitted at depth+1)
      // so each one doesn't render its own "did not include Plotly traces" error
      // box. Only substitute when at least one real chart remains, so a fully
      // dataless response still surfaces its error as before.
      var __renderableGraphs = filterRenderableGraphFrames(plotlyJSON['graphs']);
      if (__renderableGraphs.length > 0) { plotlyJSON['graphs'] = __renderableGraphs; }
      // create new div for each graph
      var divs = [];
      for (var i = 0; i < plotlyJSON['graphs'].length; i++) {
        var div = document.createElement('div');
        div.id = `${graphId}graph-target${i}`;
        divs.push(div);
      }
      //append all divs
      plotlyDiv.innerHTML = '';
      for (var i = 0; i < divs.length; i++) {
        plotlyDiv.appendChild(divs[i]);
      }
      //render all graphs
      for (var i = 0; i < plotlyJSON['graphs'].length; i++) {
        var plotlyJSONGraph = plotlyJSON['graphs'][i];
        var plotlyJSONLayout = getPlotlyLayoutDefaulted(plotlyJSONGraph);
        renderPlotlyMaybeLazy(divs[i], plotlyJSONGraph['data'], plotlyJSONLayout, plotlyDefaultOptions);
      }
    } else {
      var plotlyJSONLayout = getPlotlyLayoutDefaulted(plotlyJSON);
      renderPlotlyMaybeLazy(plotlyDiv, plotlyJSON['data'], plotlyJSONLayout, plotlyDefaultOptions);
    }
    //chat reposition post rendering
    $(document).scrollTop($(document).height());
    $('.mdl-layout__content').scrollTop($(document).height());
  };
}

async function getChatNameFromQuery(queryValue, responseData) {
  // Default to untitled if no query or data available
  if (!queryValue) return 'Untitled';

  try {
    // If we have metadata with a summary or verbalization, use that
    if (responseData && responseData.metadata) {
      if (responseData.metadata.summary) {
        return responseData.metadata.summary;
      }
      if (responseData.metadata.verbalization) {
        return responseData.metadata.verbalization.slice(0, 30) + '...';
      }
    }

    // If we have plotlyJSON with a title, use that
    if (responseData && responseData.plotlyJSON) {
      if (responseData.plotlyJSON.title) {
        return responseData.plotlyJSON.title;
      }

      if (responseData.plotlyJSON.layout && responseData.plotlyJSON.layout.title) {
        if (typeof responseData.plotlyJSON.layout.title === 'string') {
          return responseData.plotlyJSON.layout.title;
        } else if (responseData.plotlyJSON.layout.title.text) {
          return responseData.plotlyJSON.layout.title.text;
        }
      }
    }

    // Otherwise, get a summary from the server
    const summaryResponse = await fetch(fixtures.inf_server_url + '/summarize-query', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query: queryValue,
        secret: window.userData.secret
      })
    });

    if (summaryResponse.ok) {
      const summaryData = await summaryResponse.json();
      if (summaryData && summaryData.summary) {
        return summaryData.summary;
      }
    }

    // If all else fails, truncate the query
    if (queryValue.length > 30) {
      return queryValue.slice(0, 30) + '...';
    }

    return queryValue;
  } catch (error) {
    console.warn('Error generating chat name:', error);
    return 'Untitled';
  }
}

function cleanGeneratedChatName(chatName) {
  if (!chatName) return '';
  return String(chatName)
    .replace(/[.#$\[\]\/\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 40);
}

function isAutoNamedChat(chatName) {
  return chatName === 'Untitled' || /^Chat\s+\d+$/i.test(chatName || '');
}

function makeUniqueChatName(chatName, chats) {
  const baseName = cleanGeneratedChatName(chatName) || 'Chat';
  if (!chats || !chats[baseName]) return baseName;

  let suffix = 2;
  let candidate = baseName + ' ' + suffix;
  while (chats[candidate]) {
    suffix += 1;
    candidate = baseName + ' ' + suffix;
  }
  return candidate;
}

async function fetchAndRenderChatForQueryInternal(queryValue, edits, graphId) {
  if (typeof edits === 'undefined' || !edits) {
    edits = [];
  }
  if (typeof edits === 'string') {
    edits = [edits];
  }
  var askfelixbutton = $('#askfelixbutton');
  askfelixbutton.attr('disabled', 'disabled');
  askfelixbutton.closest('.mdl-progress').show();
  var url = askfelixform.attr('action');
  var currentActiveChat = await dataAccessor.getCurrentActiveChat(window.userData);
  var requestBody = {
    query: queryValue,
    secret: window.userData.secret
  };

  // Add database context if available
  if (window.activeDataSource) {
    requestBody.data_source = {
      id: window.activeDataSource.id,
      table: window.activeDataSource.table
    };
  }
  if (edits && edits.length > 0) {
    url = '/edit-graph';
    requestBody.edits = edits;
  }

  var futures = [];
  var responseData = null;

  for (var editIdx = 0; editIdx < edits.length + 1; editIdx++) {
    var currentEdits = edits.slice(0, editIdx);
    if (edits && edits.length > 0 && currentEdits) {
      url = '/edit-graph';
      requestBody.edits = currentEdits;
    }
    var $form = $('#' + graphId).find('.askfelixformeditchat'); // only set if needed
    var responseProcessor = chatResponseProcessor(
      currentActiveChat,
      graphId,
      queryValue,
      edits,
      askfelixbutton
    );
    if ($form.length > 0) {
      var editQueryValue = currentEdits[currentEdits.length - 1];
      var editId = graphId + '--' + (currentEdits || []).length;
      $form
        .parent()
        .find('.chat-edit-container')
        .append(renderChatMessageTemplate(editQueryValue, editId));
      responseProcessor = chatEditResponseProcessor(
        currentActiveChat,
        editId,
        $form,
        askfelixbutton
      );
    }

    // Try to get from cache first for the current edit level
    let usedCache = false;
    if (graphCache) {
      try {
        const cachedGraphData = await graphCache.getGraph(
          queryValue,
          currentActiveChat.name,
          graphId
        );

        if (cachedGraphData) {
          console.log(`Using cached chat data for "${queryValue}" with edit level ${editIdx}`);

          // Create a mock response object to pass to the response processor
          const mockResponse = {
            ok: true,
            json: () => Promise.resolve(cachedGraphData.plotlyJSON),
            headers: {
              get: name =>
                name.toLowerCase() === 'content-type' && cachedGraphData.isImage
                  ? 'image/png'
                  : 'application/json'
            },
            blob: () => Promise.resolve(cachedGraphData.data)
          };

          // Process the cached data
          await responseProcessor(mockResponse);
          usedCache = true;

          // Store response data for auto-naming if this is the first query
          if (editIdx === 0 && !edits.length) {
            responseData = {
              plotlyJSON: cachedGraphData.plotlyJSON,
              metadata: cachedGraphData.metadata
            };
          }

          // Add a resolved promise to our futures array
          futures.push(Promise.resolve());
          continue; // Skip to the next edit level
        }
      } catch (error) {
        console.warn('Error retrieving chat from cache:', error);
        // Continue with the regular fetch if cache retrieval fails
      }
    }

    // If not in cache or cache failed, fetch from server
    if (!usedCache) {
      var resultFuture = fetchGraphEndpoint(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      })
        .then(async function (response) {
          // If this is the first query response, save the data for chat naming
          if (editIdx === 0 && !edits.length) {
            const clonedResponse = response.clone();
            try {
              const data = await clonedResponse.json();
              responseData = {
                plotlyJSON: data,
                metadata: data.metadata
              };
            } catch (e) {
              console.warn('Could not extract data for chat naming:', e);
            }
          }

          return responseProcessor(response);
        })
        .catch(function (error) {
          restoreMainQuery(queryValue);
          showSnackbarSafely({
            message: error && error.message
              ? error.message
              : 'I could not build that chart. Select a dataset and try again.'
          });
          // $('#results').html('Error: ' + error);
          $('.mdl-progress').hide();
          $('.loading-chat-bar').remove();

          askfelixbutton.removeAttr('disabled');
          console.log(error);
        });
      futures.push(resultFuture);
      // Wait for this edit to complete before processing the next one
      await resultFuture;
    }
  }

  // If this is a brand new auto-named chat and this is the first query, rename it.
  if (currentActiveChat && isAutoNamedChat(currentActiveChat.name) && !edits.length) {
    const chatMessages = currentActiveChat.messages || {};
    const messageCount = Object.keys(chatMessages).length;

    if (messageCount <= 1) {
      const generatedChatName = await getChatNameFromQuery(queryValue, responseData);
      const chats = await dataAccessor.getChats(window.userData);
      const chatName = makeUniqueChatName(generatedChatName, chats);

      // Rename the chat
      if (chatName && chatName !== currentActiveChat.name) {
        if (typeof dataAccessor.renameChat === 'function') {
          await dataAccessor.renameChat(window.userData, currentActiveChat.name, chatName);
        } else {
          await dataAccessor.newChat(window.userData, chatName);
          await dataAccessor.addMessageToChat(window.userData, chatName, graphId, queryValue);
          await dataAccessor.deleteChat(window.userData, currentActiveChat.name);
        }
        await dataAccessor.setCurrentActiveChat(window.userData, chatName);

        // Update UI
        renderChats();

        // Highlight the new chat
        $('#chatsTreeView .file').each(function (index, element) {
          if ($(element).find('.file-name').text() === chatName) {
            $(element).addClass('active');
          }
        });
      }
    }
  }

  return futures.slice(-1); // Return the last future
}
var askfelixform;

function statusBarUpdateDataset(filename) {
  var el = document.getElementById('status-dataset-name');
  if (el) el.textContent = filename || 'No dataset';
  var bar = document.getElementById('helix-status-bar');
  if (bar) bar.classList.toggle('helix-status-bar--empty', !filename);
}

async function extractResponseData(response) {
  if (!response) {
    throw new Error('The analyst service returned no response. Try again.');
  }

  // Check if response is an image/png or jpeg.
  var isImage = false;
  var data = {};
  var contentType = '';
  try {
    contentType = response.headers && typeof response.headers.get === 'function'
      ? response.headers.get('content-type') || ''
      : '';
  } catch (headerError) {
    console.warn('Unable to read analyst response headers:', headerError);
  }

  if (contentType.indexOf('image') > -1) {
    //todo render image
    data = response.blob();
    isImage = true;
  } else {
    try {
      data = await response.json();
    } catch (parseError) {
      throw new Error('The analyst returned an unreadable response. Try again.');
    }
  }

  if (response.ok === false) {
    throw new Error(getGraphErrorMessage(data, `The analyst request failed with status ${response.status || 'unknown'}.`));
  }
  if (!isImage && (!data || typeof data !== 'object')) {
    throw new Error('The analyst returned an empty response. Try again.');
  }
  var plotlyJSON = data;

  var metadata = plotlyJSON.metadata || {};
  delete plotlyJSON.metadata;
  console.log(plotlyJSON);

  // console.log(plotlyJSON);
  var plotlyChartType = '';
  try {
    if (
      plotlyJSON['graphs'] &&
      plotlyJSON['graphs'][0] &&
      plotlyJSON['graphs'][0].data &&
      plotlyJSON['graphs'][0].data[0]
    ) {
      plotlyChartType = plotlyJSON['graphs'][0].data[0].type;
    } else if (plotlyJSON.data && plotlyJSON.data[0]) {
      plotlyChartType = plotlyJSON.data[0].type;
    }
  } catch (e) {
    console.log('Error extracting chart type:', e);
    console.log(plotlyJSON);
  }
  return { isImage, data, plotlyJSON, metadata, plotlyChartType };
}

function chatEditResponseProcessor(currentChat, graphId, $form, askfelixbutton) {
  return async function (response) {
    $form.find('.loading-progress').hide();
    $('.loading-chat-bar').remove();
    askfelixbutton.removeAttr('disabled');

    var currentChatPostRequestName = await dataAccessor.getCurrentActiveChatName(window.userData);
    // check if the current dashboard is the same, otherwise dont put in another chart in this dashboard
    if (currentChatPostRequestName !== currentChat.name) {
      return;
    }
    var { isImage, data, plotlyJSON, metadata, plotlyChartType } =
      await extractResponseData(response);
    metadata = metadata || {};
    plotlyJSON = plotlyJSON || {};
    var $appendTarget = $form.parent().find('.chat-edit-container');
    addNewHTMLToChat(getChatEditTemplate(graphId), graphId, $appendTarget);
    var plotlyDiv = $appendTarget.find(`.${graphId}graph-target`)[0];

    // Add check to ensure plotlyDiv exists
    if (!plotlyDiv) {
      console.error(`DOM element for graph ${graphId} not found`);
      return;
    }

    componentHandler.upgradeDom(); // setup loading bar
    // let $formTypahead = $('.askfelixformedit[data-id="' + graphId + '"] .typeahead');

    // Add pipeline logs if available
    if (metadata && metadata.pipeline_logs && metadata.pipeline_logs.length > 0) {
      // Remove existing logs if present
      $(plotlyDiv).siblings('.pipeline-logs-container').remove();

      var logsHtml = renderPipelineLogsTemplate(
        metadata.pipeline_logs,
        metadata.reasoning_trace || {},
        metadata.reasoning_judge || null
      );
      var logsDiv = document.createElement('div');
      logsDiv.innerHTML = logsHtml;
      plotlyDiv.parentNode.insertBefore(logsDiv, plotlyDiv.nextSibling);

      // Add click handler for toggling logs display
      $('.pipeline-logs-header').on('click', function () {
        $(this).next('.pipeline-logs-content').slideToggle(200);
      });
    }

    //check it data is an image
    if (isImage) {
      var blob = await data;
      // use filereader to read base64 url
      var base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      // var url = URL.createObjectURL(blob);
      var img = document.createElement('img');
      img.src = base64;
      img.style.width = '100%';
      img.style.height = '100%';
      plotlyDiv.innerHTML = '';

      plotlyDiv.appendChild(img);
    } else if (plotlyJSON['embed']) {
      renderTableInto(plotlyDiv, { payload: plotlyJSON });
    } else if (metadata['graph_type'] == 'text_summary') {
      var text = document.createElement('p');
      text.className = 'demo-content mdl-color--white felix-dashboard-chat';
      text.innerHTML =
        '<img id="ask-felix-robot-icon" class="ask-felix-robot-icon ask-felix-robot-icon--tooltip ask-felix-robot-icon--tooltip-dashboard" src="/static/img/askfelix-robot-icon-transp.webp" alt="A" style="">' +
        plotlyJSON['text'];
      plotlyDiv.innerHTML = '';

      plotlyDiv.appendChild(text); // add askfelix logo?
    } else if (metadata['graph_type'] == 'table' && !isPlotlyTablePayload(plotlyJSON)) {
      if (window.NativeDatasetEditor && typeof window.NativeDatasetEditor.renderTableInto === 'function') {
        renderTableInto(plotlyDiv, { payload: plotlyJSON });
        return;
      }
      // Render table data with enhanced functionality
      var tableContainer = document.createElement('div');
      tableContainer.className = 'table-container';

      if (plotlyJSON.data && plotlyJSON.data.length > 0) {
        var tableColumns = plotlyJSON.columns || Object.keys(plotlyJSON.data[0]);

        // Create DataTable-compatible structure
        var table = document.createElement('table');
        table.className = 'display stripe hover';
        table.style.width = '100%';
        var tableId = 'datatable-' + Math.random().toString(36).substring(7);
        table.id = tableId;

        // Create header
        var thead = document.createElement('thead');
        var headerRow = document.createElement('tr');
        tableColumns.forEach(function (column) {
          var th = document.createElement('th');
          th.textContent = column;
          headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Create body
        var tbody = document.createElement('tbody');
        plotlyJSON.data.forEach(function (row) {
          var tr = document.createElement('tr');
          tableColumns.forEach(function (column) {
            var td = document.createElement('td');
            td.textContent = row[column] || '';
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);

        tableContainer.appendChild(table);

        // Add verbalization if available
        if (plotlyJSON.verbalization) {
          var verbDiv = document.createElement('div');
          verbDiv.className = 'table-verbalization';
          verbDiv.style.marginTop = '10px';
          verbDiv.style.fontStyle = 'italic';
          verbDiv.style.color = '#666';
          verbDiv.textContent = plotlyJSON.verbalization;
          tableContainer.appendChild(verbDiv);
        }

        plotlyDiv.innerHTML = '';
        plotlyDiv.appendChild(tableContainer);

        // Initialize DataTable with enhanced features
        function initDataTable() {
          if (typeof $ !== 'undefined' && $.fn.DataTable) {
            $('#' + tableId).DataTable({
              pageLength: 25,
              lengthMenu: [
                [10, 25, 50, 100, -1],
                [10, 25, 50, 100, 'All']
              ],
              responsive: true,
              scrollX: true,
              order: [],
              columnDefs: [
                {
                  targets: '_all',
                  className: 'dt-body-left'
                }
              ],
              dom: '<"top"fli>rt<"bottom"p><"clear">',
              language: {
                search: 'Search table:',
                info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                lengthMenu: 'Show _MENU_ entries'
              }
            });
          } else {
            // Fallback: Load DataTables dynamically if not available
            var link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.datatables.net/1.13.7/css/jquery.dataTables.min.css';
            document.head.appendChild(link);

            var script = document.createElement('script');
            script.src = 'https://cdn.datatables.net/1.13.7/js/jquery.dataTables.min.js';
            script.onload = function () {
              $('#' + tableId).DataTable({
                pageLength: 25,
                lengthMenu: [
                  [10, 25, 50, 100, -1],
                  [10, 25, 50, 100, 'All']
                ],
                responsive: true,
                scrollX: true,
                order: [],
                columnDefs: [
                  {
                    targets: '_all',
                    className: 'dt-body-left'
                  }
                ],
                dom: '<"top"fli>rt<"bottom"p><"clear">',
                language: {
                  search: 'Search table:',
                  info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                  lengthMenu: 'Show _MENU_ entries'
                }
              });
            };
            document.head.appendChild(script);
          }
        }

        // Initialize table after DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initDataTable);
        } else {
          setTimeout(initDataTable, 100);
        }
      } else {
        tableContainer.innerHTML = '<div class="no-data">No table data available</div>';
        plotlyDiv.innerHTML = '';
        plotlyDiv.appendChild(tableContainer);
      }
    } else if (plotlyJSON['graphs'] && plotlyJSON['graphs'].length > 0) {
      // Drop dataless frames (e.g. extra LLM/analysis frames emitted at depth+1)
      // so each one doesn't render its own "did not include Plotly traces" error
      // box. Only substitute when at least one real chart remains, so a fully
      // dataless response still surfaces its error as before.
      var __renderableGraphs = filterRenderableGraphFrames(plotlyJSON['graphs']);
      if (__renderableGraphs.length > 0) { plotlyJSON['graphs'] = __renderableGraphs; }
      // create new div for each graph
      var divs = [];
      for (var i = 0; i < plotlyJSON['graphs'].length; i++) {
        var div = document.createElement('div');
        div.id = `${graphId}graph-target${i}`;
        divs.push(div);
      }
      //append all divs
      plotlyDiv.innerHTML = '';
      for (var i = 0; i < divs.length; i++) {
        plotlyDiv.appendChild(divs[i]);
      }
      //render all graphs
      for (var i = 0; i < plotlyJSON['graphs'].length; i++) {
        try {
          var plotlyJSONGraph = plotlyJSON['graphs'][i];
          var plotlyJSONLayout = getPlotlyLayoutDefaulted(plotlyJSONGraph);
          renderPlotlyMaybeLazy(
            divs[i],
            plotlyJSONGraph['data'],
            plotlyJSONLayout,
            plotlyDefaultOptions
          );
        } catch (e) {
          console.error(`Error rendering graph ${i}:`, e);
          showGraphRenderError(
            divs[i],
            'Could not render this visualization.',
            e && e.message ? e.message : String(e || '')
          );
        }
      }
    } else {
      var plotlyJSONLayout = getPlotlyLayoutDefaulted(plotlyJSON);
      renderPlotlyMaybeLazy(plotlyDiv, plotlyJSON['data'], plotlyJSONLayout, plotlyDefaultOptions);
    }
    $(document).scrollTop($(document).height());
    $('.mdl-layout__content').scrollTop($(document).height());
  };
}

function editResponseProcessor(graphId, $form, askfelixbutton) {
  return async function (response) {
    //check if response is an image/png or jpeg
    var { isImage, data, plotlyJSON, metadata, plotlyChartType } =
      await extractResponseData(response);
    metadata = metadata || {};
    plotlyJSON = plotlyJSON || {};

    var plotlyDiv = document.getElementById(`${graphId}graph-target`);

    // Check if plotlyDiv exists
    if (!plotlyDiv) {
      console.error(`DOM element for graph ${graphId} not found`);
      return;
    }

    // Add pipeline logs if available
    if (metadata && metadata.pipeline_logs && metadata.pipeline_logs.length > 0) {
      // Remove existing logs if present
      $(plotlyDiv).siblings('.pipeline-logs-container').remove();

      var logsHtml = renderPipelineLogsTemplate(
        metadata.pipeline_logs,
        metadata.reasoning_trace || {},
        metadata.reasoning_judge || null
      );
      var logsDiv = document.createElement('div');
      logsDiv.innerHTML = logsHtml;
      plotlyDiv.parentNode.insertBefore(logsDiv, plotlyDiv.nextSibling);

      // Add click handler for toggling logs display
      $('.pipeline-logs-header').on('click', function () {
        $(this).next('.pipeline-logs-content').slideToggle(200);
      });
    }

    //check it data is an image
    if (isImage) {
      var blob = await data;
      // use filereader to read base64 url
      var base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      // var url = URL.createObjectURL(blob);
      var img = document.createElement('img');
      img.src = base64;
      img.style.width = '100%';
      img.style.height = '100%';
      plotlyDiv.innerHTML = '';

      plotlyDiv.appendChild(img);
    } else if (plotlyJSON['embed']) {
      renderTableInto(plotlyDiv, { payload: plotlyJSON });
    } else if (metadata['graph_type'] == 'text_summary') {
      var text = document.createElement('p');
      text.className = 'demo-content mdl-color--white felix-dashboard-chat';
      text.innerHTML =
        '<img id="ask-felix-robot-icon" class="ask-felix-robot-icon ask-felix-robot-icon--tooltip ask-felix-robot-icon--tooltip-dashboard" src="/static/img/askfelix-robot-icon-transp.webp" alt="A" style="">' +
        plotlyJSON['text'];
      plotlyDiv.innerHTML = '';

      plotlyDiv.appendChild(text); // add askfelix logo?
    } else if (metadata['graph_type'] == 'table' && !isPlotlyTablePayload(plotlyJSON)) {
      if (window.NativeDatasetEditor && typeof window.NativeDatasetEditor.renderTableInto === 'function') {
        renderTableInto(plotlyDiv, { payload: plotlyJSON });
        return;
      }
      // Render table data with enhanced functionality
      var tableContainer = document.createElement('div');
      tableContainer.className = 'table-container';

      if (plotlyJSON.data && plotlyJSON.data.length > 0) {
        var tableColumns = plotlyJSON.columns || Object.keys(plotlyJSON.data[0]);

        // Create DataTable-compatible structure
        var table = document.createElement('table');
        table.className = 'display stripe hover';
        table.style.width = '100%';
        var tableId = 'datatable-' + Math.random().toString(36).substring(7);
        table.id = tableId;

        // Create header
        var thead = document.createElement('thead');
        var headerRow = document.createElement('tr');
        tableColumns.forEach(function (column) {
          var th = document.createElement('th');
          th.textContent = column;
          headerRow.appendChild(th);
        });
        thead.appendChild(headerRow);
        table.appendChild(thead);

        // Create body
        var tbody = document.createElement('tbody');
        plotlyJSON.data.forEach(function (row) {
          var tr = document.createElement('tr');
          tableColumns.forEach(function (column) {
            var td = document.createElement('td');
            td.textContent = row[column] || '';
            tr.appendChild(td);
          });
          tbody.appendChild(tr);
        });
        table.appendChild(tbody);

        tableContainer.appendChild(table);

        // Add verbalization if available
        if (plotlyJSON.verbalization) {
          var verbDiv = document.createElement('div');
          verbDiv.className = 'table-verbalization';
          verbDiv.style.marginTop = '10px';
          verbDiv.style.fontStyle = 'italic';
          verbDiv.style.color = '#666';
          verbDiv.textContent = plotlyJSON.verbalization;
          tableContainer.appendChild(verbDiv);
        }

        plotlyDiv.innerHTML = '';
        plotlyDiv.appendChild(tableContainer);

        // Initialize DataTable with enhanced features
        function initDataTable() {
          if (typeof $ !== 'undefined' && $.fn.DataTable) {
            $('#' + tableId).DataTable({
              pageLength: 25,
              lengthMenu: [
                [10, 25, 50, 100, -1],
                [10, 25, 50, 100, 'All']
              ],
              responsive: true,
              scrollX: true,
              order: [],
              columnDefs: [
                {
                  targets: '_all',
                  className: 'dt-body-left'
                }
              ],
              dom: '<"top"fli>rt<"bottom"p><"clear">',
              language: {
                search: 'Search table:',
                info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                lengthMenu: 'Show _MENU_ entries'
              }
            });
          } else {
            // Fallback: Load DataTables dynamically if not available
            var link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdn.datatables.net/1.13.7/css/jquery.dataTables.min.css';
            document.head.appendChild(link);

            var script = document.createElement('script');
            script.src = 'https://cdn.datatables.net/1.13.7/js/jquery.dataTables.min.js';
            script.onload = function () {
              $('#' + tableId).DataTable({
                pageLength: 25,
                lengthMenu: [
                  [10, 25, 50, 100, -1],
                  [10, 25, 50, 100, 'All']
                ],
                responsive: true,
                scrollX: true,
                order: [],
                columnDefs: [
                  {
                    targets: '_all',
                    className: 'dt-body-left'
                  }
                ],
                dom: '<"top"fli>rt<"bottom"p><"clear">',
                language: {
                  search: 'Search table:',
                  info: 'Showing _START_ to _END_ of _TOTAL_ entries',
                  lengthMenu: 'Show _MENU_ entries'
                }
              });
            };
            document.head.appendChild(script);
          }
        }

        // Initialize table after DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', initDataTable);
        } else {
          setTimeout(initDataTable, 100);
        }
      } else {
        tableContainer.innerHTML = '<div class="no-data">No table data available</div>';
        plotlyDiv.innerHTML = '';
        plotlyDiv.appendChild(tableContainer);
      }
    } else if (plotlyJSON['graphs'] && plotlyJSON['graphs'].length > 0) {
      // Drop dataless frames (e.g. extra LLM/analysis frames emitted at depth+1)
      // so each one doesn't render its own "did not include Plotly traces" error
      // box. Only substitute when at least one real chart remains, so a fully
      // dataless response still surfaces its error as before.
      var __renderableGraphs = filterRenderableGraphFrames(plotlyJSON['graphs']);
      if (__renderableGraphs.length > 0) { plotlyJSON['graphs'] = __renderableGraphs; }
      // create new div for each graph
      var divs = [];
      for (var i = 0; i < plotlyJSON['graphs'].length; i++) {
        var div = document.createElement('div');
        div.id = `${graphId}graph-target${i}`;
        divs.push(div);
      }
      //append all divs
      plotlyDiv.innerHTML = '';
      for (var i = 0; i < divs.length; i++) {
        plotlyDiv.appendChild(divs[i]);
      }
      //render all graphs
      for (var i = 0; i < plotlyJSON['graphs'].length; i++) {
        var plotlyJSONGraph = plotlyJSON['graphs'][i];
        var plotlyJSONLayout = getPlotlyLayoutDefaulted(plotlyJSONGraph);
        renderPlotlyMaybeLazy(divs[i], plotlyJSONGraph['data'], plotlyJSONLayout, plotlyDefaultOptions);
      }
    } else {
      var plotlyJSONLayout = getPlotlyLayoutDefaulted(plotlyJSON);
      renderPlotlyMaybeLazy(plotlyDiv, plotlyJSON['data'], plotlyJSONLayout, plotlyDefaultOptions);
    }
  };
}

function updateHistoryNavHint(idx, total) {
  var hint = document.getElementById('query-history-nav-hint');
  if (!hint) {
    hint = document.createElement('div');
    hint.id = 'query-history-nav-hint';
    hint.className = 'query-history-nav-hint';
    var remote = document.getElementById('remote');
    if (remote) remote.appendChild(hint);
  }
  hint.textContent = idx === -1 ? '' : '\u2191\u2193 history (' + (idx + 1) + '/' + total + ')';
  hint.style.opacity = idx === -1 ? '0' : '1';
}

function hideHistoryNavHint() {
  updateHistoryNavHint(-1, 0);
}

$(document).ready(function () {
  // Initialize the graph cache system
  if (typeof graphCache !== 'undefined') {
    graphCache
      .init()
      .then(stats => {
        console.log('Graph cache initialized:', stats);
        // Only create control panel in development environments
        if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
          graphCache.createCacheControlPanel();
        }
      })
      .catch(error => {
        console.warn('Failed to initialize graph cache:', error);
      });
  }

  // Initialize memory manager for heavy charts if available
  if (typeof graphMemoryManager !== 'undefined') {
    graphMemoryManager.init({
      maxHeavyCharts: 3, // Max number of heavy charts to keep loaded
      heavyChartThreshold: 5000, // Number of datapoints to consider a chart "heavy"
      checkInterval: 2000, // Check visibility every 2 seconds
      debugMode: location.hostname === 'localhost' || location.hostname === '127.0.0.1'
    });
  }

  askfelixform = $('#askfelixform');

  // Add enter key handler for the main query input. Shift+Enter inserts a
  // newline in the textarea; plain Enter submits the form.
  $('#query').on('keypress', function (e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      askfelixform.submit();
    }
  });

  // History arrow navigation state
  var historyNavIndex = -1;
  var preNavValue = '';

  $(document).on('keydown', '#query', function (e) {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      var qhm = window.queryHistoryManager;
      if (!qhm || !qhm.historyCache || qhm.historyCache.length === 0) return;
      var cache = qhm.historyCache;

      if (historyNavIndex === -1) {
        preNavValue = $(this).val();
      }

      if (e.key === 'ArrowUp') {
        historyNavIndex = Math.min(historyNavIndex + 1, cache.length - 1);
      } else {
        historyNavIndex = historyNavIndex - 1;
      }

      if (historyNavIndex < 0) {
        historyNavIndex = -1;
        $(this).val(preNavValue);
      } else {
        $(this).val(cache[historyNavIndex].query_text || cache[historyNavIndex]);
      }
      updateHistoryNavHint(historyNavIndex, cache.length);
    } else if (e.key !== 'Enter') {
      historyNavIndex = -1;
      hideHistoryNavHint();
    }
  });

  askfelixform.off('submit.askfelixQuery').on('submit.askfelixQuery', function (e) {
    e.preventDefault();
    e.stopPropagation();

    // Check query limit before proceeding
    if (!checkQueryLimit()) {
      return false;
    }

    var $queryInput = $('#query');
    var queryValue = $queryInput.val();
    if (!queryValue || !queryValue.trim()) {
      askfelixform.find('.typeahead.tt-input').focus();
      return false;
    }

    // Clear the input immediately after capturing the query. queryValue is
    // already captured by value, so the in-flight request is unaffected. This
    // gives chat-style "sent" feedback and, crucially, prevents a second Enter
    // press (or accidental double submit) from re-running the same query as a
    // duplicate graph/message: the second submit now sees an empty box and
    // bails out above.
    $queryInput.val('');
    historyNavIndex = -1;
    if (typeof hideHistoryNavHint === 'function') hideHistoryNavHint();
    // Notify autogrow/autocomplete that the field is now empty so the textarea
    // collapses back to one row and any open suggestion menu hides.
    var queryEl = $queryInput.get(0);
    if (queryEl) queryEl.dispatchEvent(new Event('input', { bubbles: true }));

    var graphId = Math.random().toString(36).substring(7);
    // if is in a dashboard, add to dashboard
    if (dataAccessor.isInChat) {
      const finishedFuture = fetchAndRenderChatForQuery(queryValue, [], graphId);
      finishedFuture.then(function () {
        return dataAccessor.addMessageToCurrentActiveChat(window.userData, graphId, queryValue);
      }).catch(function (error) {
        // Rendering succeeded already; a transient save failure must not become
        // an unhandled rejection or break the remaining chat UI.
        console.warn('Unable to persist rendered chat message:', error);
      });
    } else {
      const finishedFuture = fetchAndRenderGraphForQuery(queryValue, [], graphId);
      finishedFuture.then(function () {
        return dataAccessor.addGraphToCurrentDashboard(window.userData, graphId, queryValue);
      }).catch(function (error) {
        console.warn('Unable to persist rendered dashboard graph:', error);
      });
    }
  });

  document.addEventListener('dataset:selected', function (e) {
    if (e.detail && e.detail.filename) {
      window['currentDatasetName'] = e.detail.filename;
      statusBarUpdateDataset(e.detail.filename);
    } else {
      window['currentDatasetName'] = '';
      statusBarUpdateDataset(null);
    }
  });

  document.addEventListener('keydown', function (e) {
    var tag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
    var isEditable =
      tag === 'input' ||
      tag === 'textarea' ||
      tag === 'select' ||
      document.activeElement.isContentEditable;

    if (e.ctrlKey && !e.shiftKey && e.key === 'Enter') {
      if (document.activeElement === document.getElementById('query')) {
        e.preventDefault();
        var form = document.getElementById('askfelixform');
        if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
      return;
    }

    if (e.ctrlKey && !e.shiftKey && e.key === 'k') {
      e.preventDefault();
      var queryInput = document.getElementById('query');
      if (queryInput) queryInput.focus();
      return;
    }

    if (e.ctrlKey && e.shiftKey && e.key === 'S') {
      e.preventDefault();
      window.location.href = '/sqlworkbench';
      return;
    }

    if (e.key === '?' && !isEditable) {
      e.preventDefault();
      var dialog = document.getElementById('keyboard-shortcuts-dialog');
      if (!dialog) {
        dialog = document.createElement('dialog');
        dialog.id = 'keyboard-shortcuts-dialog';
        dialog.className = 'keyboard-shortcuts-dialog';
        dialog.innerHTML =
          '<h3><span class="material-icons" style="vertical-align:middle;margin-right:8px;font-size:18px;">keyboard</span>Keyboard Shortcuts</h3>' +
          '<table>' +
          '<tr><td>Ctrl+K</td><td>Focus query bar</td></tr>' +
          '<tr><td>Ctrl+Enter</td><td>Run query</td></tr>' +
          '<tr><td>Ctrl+Shift+S</td><td>Open SQL Workbench</td></tr>' +
          '<tr><td>?</td><td>Show this help</td></tr>' +
          '</table>' +
          '<div class="dialog-close-row">' +
          '<button type="button" class="mdl-button close dialog-close-icon" aria-label="Close dialog" title="Close dialog"><span aria-hidden="true">&times;</span></button>' +
          '</div>';
        document.body.appendChild(dialog);
        dialog.querySelector('.dialog-close-icon').addEventListener('click', function () {
          dialog.close();
        });
      }
      if (dialog.open) {
        dialog.close();
      } else if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.show();
      }
      return;
    }
  });

  $(document).on('submit', '.askfelixformedit', async function (e) {
    e.preventDefault();

    e.stopPropagation();

    var $form = $(this);
    var graphId = $form.data('id');

    var editQueryValue = $form.find('#editquery').val();
    if (!editQueryValue) {
      $form.find('.typeahead.tt-input').focus();
      return false;
    }
    var currentActiveDashboardName = await dataAccessor.getCurrentActiveDashboardName(
      window.userData
    );
    dataAccessor
      .getGraphData(window.userData, currentActiveDashboardName, graphId)
      .then(function (graphData) {
        // graphData.edits and graphData.graphName describe the graph
        var currentGraphData = graphData;
        currentGraphData.edits = currentGraphData.edits || [];
        currentGraphData.edits.push(editQueryValue);
        // Update retry info with the latest query and edits
        graphRetryInfo[graphId] = {
          query: currentGraphData.graphName,
          edits: currentGraphData.edits
        };
        dataAccessor
          .updateGraphData(window.userData, currentActiveDashboardName, graphId, currentGraphData)
          .then(function () {
            //todo errr handling for firebase fails to save?
          });

        var askfelixbutton = $form.find('.askfelixbutton');
        askfelixbutton.attr('disabled', 'disabled');
        $form.find('.loading-progress').show();
        fetchGraphEndpoint($form.attr('action'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: currentGraphData.graphName,
            secret: window.userData.secret, // todo wait
            edits: currentGraphData.edits
          })
        })
          .then(editResponseProcessor(graphId, $form, askfelixbutton))
          .catch(function (error) {
            console.error('Error:', error);
            // $('#results').html('Error: ' + error);
            showSnackbarSafely({
              message: error && error.message
                ? error.message
                : 'The chart edit failed. Mention the chart type or columns and retry.'
            });

            $form.find('.loading-progress').hide();
            $('.loading-chat-bar').remove();
            askfelixbutton.removeAttr('disabled');
          })
          .finally(function () {
            $form.find('.loading-progress').hide();
            $('.loading-chat-bar').remove();
            askfelixbutton.removeAttr('disabled');
          });
      });
  });

  //chat based askfelixformedit

  $(document).on('submit', '.askfelixformeditchat', async function (e) {
    e.preventDefault();

    e.stopPropagation();

    var $form = $(this);
    var graphId = $form.data('id');

    var editQueryValue = $form.find('#editchatquery').val();
    if (!editQueryValue) {
      $form.find('.typeahead.tt-input').focus();
      return false;
    }
    var currentActiveChat = await dataAccessor.getCurrentChat(window.userData);

    dataAccessor
      .getChatMessage(window.userData, currentActiveChat.name, graphId)
      .then(function (graphData) {
        var editId = graphId + '--' + (graphData.edits || []).length + 1; //add 1 to increment edit query soon
        $form
          .parent()
          .find('.chat-edit-container')
          .append(renderChatMessageTemplate(editQueryValue, editId));
        $(document).scrollTop($(document).height());
        $('.mdl-layout__content').scrollTop($(document).height());
        // graphData.edits and graphData.graphName describe the graph
        var currentGraphData = graphData;
        currentGraphData.edits = currentGraphData.edits || [];
        currentGraphData.edits.push(editQueryValue);
        // Update retry info for chat edits as well
        graphRetryInfo[graphId] = {
          query: currentGraphData.messageName,
          edits: currentGraphData.edits
        };
        dataAccessor
          .updateChatData(window.userData, currentActiveChat.name, graphId, currentGraphData)
          .then(function () {
            //todo errr handling for firebase fails to save?
          });

        var askfelixbutton = $form.find('.askfelixbutton');
        askfelixbutton.attr('disabled', 'disabled');
        $form.find('.loading-progress').show();
        fetchGraphEndpoint($form.attr('action'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            query: currentGraphData.messageName,
            secret: window.userData.secret, // todo wait
            edits: currentGraphData.edits
          })
        })
          .then(chatEditResponseProcessor(currentActiveChat, editId, $form, askfelixbutton))
          .catch(function (error) {
            console.error('Error:', error);
            // $('#results').html('Error: ' + error);
            showSnackbarSafely({
              message: error && error.message
                ? error.message
                : 'The chart edit failed. Mention the chart type or columns and retry.'
            });

            $form.find('.loading-progress').hide();
            $('.loading-chat-bar').remove();
            askfelixbutton.removeAttr('disabled');
          });
      });
  });

  // Initialize pipeline logs display functionality
  $(document).on('click', '.pipeline-logs-header', function (e) {
    // Don't toggle if clicking on the copy button
    if ($(e.target).closest('.copy-logs-button').length) {
      return;
    }

    $(this).next('.pipeline-logs-content').slideToggle(200);

    // Change icon based on state
    const icon = $(this).find('.pipeline-logs-toggle .material-icons');
    if ($(this).next('.pipeline-logs-content').is(':visible')) {
      icon.text('expand_less');
    } else {
      icon.text('psychology');
    }
  });

  // Handle copy logs button click
  $(document).on('click', '.copy-logs-button', function (e) {
    e.stopPropagation(); // Prevent toggling the logs container

    const $logsContainer = $(this).closest('.pipeline-logs-container');
    const $logsContent = $logsContainer.find('.pipeline-logs-content');

    // Ensure logs content is visible while copying
    const wasHidden = !$logsContent.is(':visible');
    if (wasHidden) {
      $logsContent.show();
    }

    // Extract log text in a readable format
    let logText = 'Reasoning Trace\n\n';

    const overviewLines = [];
    $logsContent.find('.pipeline-logs-overview-row').each(function () {
      const label = $(this).find('.pipeline-logs-overview-label').text();
      const value = $(this).find('.pipeline-logs-overview-value').text();
      if (label && value) {
        overviewLines.push(`${label}: ${value}`);
      }
    });

    const summaryLines = [];
    $logsContent.find('.pipeline-logs-summary-list li').each(function () {
      summaryLines.push($(this).text());
    });
    const fallbackLines = [];
    $logsContent.find('.pipeline-fallback-list li').each(function () {
      fallbackLines.push($(this).text());
    });
    const judgeLines = [];
    const $judge = $logsContent.find('.pipeline-logs-judge');
    if ($judge.length) {
      const verdict = $judge.find('.pipeline-logs-judge-label').text();
      const score = $judge.find('.pipeline-logs-judge-score').text();
      judgeLines.push(`Judge verdict: ${verdict}${score ? ' (' + score + ')' : ''}`);
      $judge.find('.pipeline-logs-judge-improvements li').each(function () {
        judgeLines.push(`Suggestion: ${$(this).text()}`);
      });
    }

    if (summaryLines.length) {
      logText += 'Summary:\n';
      logText += summaryLines.join('\n') + '\n\n';
    }
    if (overviewLines.length) {
      logText += 'Overview:\n';
      logText += overviewLines.join('\n') + '\n\n';
    }
    if (fallbackLines.length) {
      logText += 'Fallbacks:\n';
      logText += fallbackLines.join('\n') + '\n\n';
    }
    if (judgeLines.length) {
      logText += judgeLines.join('\n') + '\n\n';
    }

    $logsContent.find('.pipeline-log-item').each(function () {
      const step = $(this).find('.pipeline-log-step').text();
      const message = $(this).find('.pipeline-log-message').text();

      logText += `STEP: ${step}\n`;
      logText += `${message}\n`;

      // Add code examples if present
      const $codeBlock = $(this).find('.pipeline-log-code pre code');
      if ($codeBlock.length) {
        logText += '\nCode Example:\n';
        logText += $codeBlock.text() + '\n';
      }

      logText += '\n---\n\n';
    });

    // Copy to clipboard
    const textArea = document.createElement('textarea');
    textArea.value = logText;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);

    // Visual feedback
    const $icon = $(this).find('.material-icons');
    const originalText = $icon.text();
    $icon.text('check');
    setTimeout(() => {
      $icon.text(originalText);
    }, 2000);

    // Hide logs again if they were hidden before
    if (wasHidden) {
      $logsContent.hide();
    }
  });

  // Add syntax highlighting for code examples
  function highlightCode() {
    if (typeof hljs !== 'undefined') {
      document.querySelectorAll('.pipeline-log-code pre code').forEach(block => {
        hljs.highlightBlock(block);
      });
    }
  }

  // Syntax highlighting is optional. On some mobile page lifecycle paths the
  // ready callback can run without a body, which is not a valid observer target.
  if (typeof MutationObserver === 'function' && document.body) {
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          // Check for our target node in added nodes
          for (let i = 0; i < mutation.addedNodes.length; i++) {
            const node = mutation.addedNodes[i];
            if (node.nodeType === 1 && node.querySelector('.pipeline-log-code')) {
              highlightCode();
              return;
            }
          }
        }
      });
    });

    try {
      observer.observe(document.body, { childList: true, subtree: true });
    } catch (error) {
      // Do not prevent the rest of the chart UI from initializing if a browser
      // invalidates the target during a page lifecycle transition.
      observer.disconnect();
      console.warn('Pipeline log highlighting observer could not start:', error);
    }
  }

  // Handle toggling the graph edit options panel
  $(document).on('click', '.panel-edit-options-button', function () {
    const graphId = $(this).data('id');
    $(`#${graphId}-edit-options`).slideToggle(200);
  });

  // Fullscreen toggle per graph
  $(document).on('click', '.panel-fullscreen-button', function () {
    const graphId = $(this).data('id');
    const $item = $(`.grid-stack-item:has(#${graphId}graph-target)`);
    if ($item.length === 0) return;
    $item.toggleClass('graph-fullscreen');
    // Swap icon
    const $icon = $(this);
    if ($item.hasClass('graph-fullscreen')) {
      $icon.text('fullscreen_exit');
    } else {
      $icon.text('fullscreen');
    }
    // Trigger relayout for plotly
    try {
      const el = document.getElementById(`${graphId}graph-target`);
      if (el && typeof Plotly !== 'undefined') {
        if (el.id) Plotly.relayout(el.id, {});
        Plotly.Plots && Plotly.Plots.resize && Promise.resolve(Plotly.Plots.resize(el)).catch(() => {});
      }
    } catch (e) {}
  });

  // Copy chart as PNG to clipboard
  $(document).on('click', '.panel-copy-png-button', function () {
    const graphId = $(this).data('id');
    const el = document.getElementById(graphId + 'graph-target');
    if (!el) return;

    Plotly.toImage(el, { format: 'png', width: el.offsetWidth || 800, height: el.offsetHeight || 400 })
      .then(function (dataUrl) {
        return fetch(dataUrl);
      })
      .then(function (res) { return res.blob(); })
      .then(function (blob) {
        if (navigator.clipboard && navigator.clipboard.write) {
          return navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        } else {
          // Fallback: open in new tab
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          return Promise.resolve();
        }
      })
      .then(function () {
        if (window.snacks) window.snacks.showSnackbar({ message: 'Chart copied to clipboard', timeout: 2500 });
      })
      .catch(function (err) {
        console.error('Copy PNG failed:', err);
        if (window.snacks) window.snacks.showSnackbar({ message: 'Could not copy chart', timeout: 3000 });
      });
  });

  // Download chart data as CSV
  $(document).on('click', '.panel-download-csv-button', function () {
    const graphId = $(this).data('id');
    let plotlyJSON = graphPlotlyCache.get(graphId);

    if (!plotlyJSON) {
      // Try to get data from Plotly element directly
      const el = document.getElementById(graphId + 'graph-target');
      if (el && el.data) {
        plotlyJSON = { data: el.data, layout: el.layout || {} };
      }
    }

    if (!plotlyJSON || !plotlyJSON.data || plotlyJSON.data.length === 0) {
      if (window.snacks) window.snacks.showSnackbar({ message: 'No data available to export', timeout: 3000 });
      return;
    }

    const csv = buildCsvFromPlotly(plotlyJSON);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'helix-chart-' + graphId + '.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
    if (window.snacks) window.snacks.showSnackbar({ message: 'CSV downloaded', timeout: 2000 });
  });

  // Handle closing the graph edit options panel
  $(document).on('click', '.graph-edit-close-button', function () {
    const graphId = $(this).data('graph-id');
    $(`#${graphId}-edit-options`).slideUp(200);
  });

  // Handle applying changes from the edit options panel
  $(document).on('click', '.graph-edit-apply-button', async function () {
    const graphId = $(this).data('graph-id');
    const chartType = $(`#${graphId}-chart-type`).val();
    const xAxis = $(`#${graphId}-x-axis`).val();
    const yAxis = $(`#${graphId}-y-axis`).val();
    const colorBy = $(`#${graphId}-color`).val();

    // Construct the edit command
    let editCommand = `chart_type:${chartType}`;

    // Add columns if selected
    if (xAxis || yAxis) {
      let columns = [];
      if (xAxis) columns.push(xAxis);
      if (yAxis) columns.push(yAxis);

      editCommand += `, columns:${columns.join(',')}`;
    }

    // Add color if selected
    if (colorBy) {
      editCommand += `, color:${colorBy}`;
    }

    // Submit the edit
    const $form = $(`.askfelixformedit[data-id="${graphId}"]`);
    if ($form.length) {
      $form.find('.typeahead').val(editCommand);
      $form.submit();

      // Hide the edit options panel
      $(`#${graphId}-edit-options`).slideUp(200);
    } else {
      console.error(`Form for graph ${graphId} not found`);
    }
  });

  // === Multi-select ===
  function getSelectedGraphIds() {
    var ids = [];
    document.querySelectorAll('.panel-select-checkbox:checked').forEach(function(cb) {
      ids.push(cb.getAttribute('data-id'));
    });
    return ids;
  }

  function updateBulkActionBar() {
    var ids = getSelectedGraphIds();
    var bar = document.getElementById('bulk-action-bar');
    var countEl = document.getElementById('bulk-count');
    if (!bar) return;
    if (ids.length === 0) {
      bar.style.display = 'none';
    } else {
      bar.style.display = 'flex';
      if (countEl) countEl.textContent = ids.length + ' chart' + (ids.length === 1 ? '' : 's') + ' selected';
    }
  }

  function clearAllSelections() {
    document.querySelectorAll('.panel-select-checkbox').forEach(function(cb) {
      cb.checked = false;
      var content = cb.closest('.grid-stack-item-content');
      if (content) content.classList.remove('card--selected');
    });
    updateBulkActionBar();
  }

  // Checkbox change
  $(document).on('change', '.panel-select-checkbox', function() {
    var content = $(this).closest('.grid-stack-item-content');
    content.toggleClass('card--selected', this.checked);
    updateBulkActionBar();
  });

  // Ctrl/Cmd+click on panel header to toggle selection
  $(document).on('click', '.draggable-header', function(e) {
    if (!e.ctrlKey && !e.metaKey) return;
    // Don't interfere with form/button clicks
    if (e.target.closest('button') || e.target.closest('input') || e.target.closest('form')) return;
    var cb = $(this).find('.panel-select-checkbox')[0];
    if (cb) {
      cb.checked = !cb.checked;
      $(cb).trigger('change');
    }
  });

  // Bulk delete
  $(document).on('click', '#bulk-delete-btn', function() {
    var ids = getSelectedGraphIds();
    if (ids.length === 0) return;
    if (!confirm('Delete ' + ids.length + ' chart' + (ids.length === 1 ? '' : 's') + '?')) return;

    ids.forEach(function(graphId) {
      var item = $('[data-id="' + graphId + '"].panel-select-checkbox').closest('.grid-stack-item')[0];
      if (item) {
        removeNodeFromGrid($(item));
        $(item).remove();
      }
      dataAccessor.deleteGraphFromCurrentDashboard(window.userData, graphId)
        .catch(function(e) { console.error('Delete failed for', graphId, e); });
    });
    clearAllSelections();
    if (window.snacks) window.snacks.showSnackbar({ message: 'Deleted ' + ids.length + ' chart' + (ids.length === 1 ? '' : 's'), timeout: 2500 });
  });

  // Bulk export CSV
  $(document).on('click', '#bulk-export-btn', function() {
    var ids = getSelectedGraphIds();
    if (ids.length === 0) return;

    var csvParts = [];
    ids.forEach(function(graphId) {
      var plotlyData = graphPlotlyCache.get(graphId);
      if (!plotlyData) {
        var el = document.getElementById(graphId + 'graph-target');
        if (el && el.data) plotlyData = { data: el.data, layout: el.layout || {} };
      }
      if (!plotlyData || !plotlyData.data) return;
      var csv = buildCsvFromPlotly(plotlyData);
      if (csv) csvParts.push(csv);
    });

    if (csvParts.length === 0) {
      if (window.snacks) window.snacks.showSnackbar({ message: 'No data available for selected charts', timeout: 3000 });
      return;
    }

    var blob = new Blob([csvParts.join('\n')], { type: 'text/csv;charset=utf-8;' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');
    a.href = url;
    a.download = 'helix-charts-export.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(function() { URL.revokeObjectURL(url); }, 1000);
    if (window.snacks) window.snacks.showSnackbar({ message: 'Exported ' + ids.length + ' chart' + (ids.length === 1 ? '' : 's'), timeout: 2000 });
    clearAllSelections();
  });

  // Cancel / deselect all
  $(document).on('click', '#bulk-deselect-btn', function() {
    clearAllSelections();
  });

  // Escape key clears selection
  $(document).on('keydown.bulkselect', function(e) {
    if (e.key === 'Escape') {
      var ids = getSelectedGraphIds();
      if (ids.length > 0) {
        clearAllSelections();
        e.stopPropagation();
      }
    }
  });
});
